import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { arrayRemove, arrayUnion, collection, deleteField, doc, getDoc, getDocs, onSnapshot, query, runTransaction, serverTimestamp, updateDoc, where, writeBatch } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';

import { db, storage } from '../../../shared/storage/firebase';
import { useAuth } from '../../auth/contexts/AuthContext';
import { ContactRole, School, Squad, User } from '../../../types';
import { OPS_LOGISTICS_BY_ID } from '../../ops/catalog/logisticsCatalog';
import {
  getMissingTeacherLogistics,
  LOCATION_TEACHING_LOGISTICS_FIELDS,
} from '../utils/teacherLogisticsCompleteness';
import SearchableSelect from '../../ops/components/shared/SearchableSelect';
import ConfirmationModal from './ConfirmationModal';
import { analyzeLeadTeacherAssignment, changeSquadLeadTeacher } from '../services/leadTeacherAssignment';
import { removeTeacherFromSquadEvents, syncSquadCalendarFromDetails, syncSquadCoTeachersChange } from '../utils/calendarSync';
import { normalizedSingleDaySchedule } from '../utils/singleDaySchedule';
import { DEFAULT_TZ } from '../../../shared/utils/timezone';
import { listCurriculumAdapters } from '../../curriculum/adapters/registry';
import { scaffoldedCurricula } from '../../curriculum/managedCurricula';
import { listManagedCurricula } from '../../curriculum/services/curriculumService';
import type { ManagedCurriculum } from '../../curriculum/managedCurricula';
import { createPartnerContact, listPartnerSeasons, updatePartnerContact } from '../../partners/services/partnerRepository';
import type { PartnerContact } from '../../partners/types';
import { inferSeasonFromStartDate } from '../../partners/utils/seasonAssignment';
import { programTermLabelFromId } from '../../../shared/terms/programTerms';
import { DISMISSAL_META, resolveStudentDismissal, type DismissalProcedureType } from '../utils/dismissalProcedure';
import CurriculumScheduleEditor from './CurriculumScheduleEditor';
import { hasCurriculumPlanConflict } from '../utils/curriculumPlanConflict';
import { assertCurriculumPlanPreservesProgress } from '../utils/curriculumAssignment';
import { isMusicCurriculum } from '../utils/curriculum';
import { canAccessSquad } from '../utils/squadAccess';

import '../styles/SquadDetailsPage.css';

type Mode = 'teacher' | 'admin';

type Props = {
  mode: Mode;
};

type SquadStaffRole = 'viewer' | 'content' | 'trainer' | 'class_support' | 'evaluator' | 'other';

const SQUAD_STAFF_ROLE_OPTIONS: Array<{ value: SquadStaffRole; label: string }> = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'class_support', label: 'Class support' },
  { value: 'trainer', label: 'Trainer' },
  { value: 'evaluator', label: 'Evaluator' },
  { value: 'content', label: 'Content / filming' },
  { value: 'other', label: 'Other staff' },
];

// Stripe payment record written by the checkout webhook (payments/{checkoutSessionId}).
type SquadPayment = {
  id: string;
  status?: string;
  amountCents: number;
  currency?: string;
  squadId?: string;
  studentId?: string;
  parentId?: string;
  paymentIntentId?: string | null;
  refundedAmountCents?: number;
  refundStatus?: 'refunded' | 'partially_refunded';
  refundPending?: unknown;
  createdAt?: { toMillis?: () => number; toDate?: () => Date };
};

const fmtUsdFromCents = (cents: number) => `$${(Math.max(0, Math.round(cents)) / 100).toFixed(2)}`;

const paymentRemainingCents = (p: SquadPayment) =>
  Math.max(0, Math.round(Number(p.amountCents || 0)) - Math.round(Number(p.refundedAmountCents || 0)));

const isRefundablePayment = (p: SquadPayment) =>
  String(p.status || '') === 'succeeded' && Boolean(String(p.paymentIntentId || '').trim()) && paymentRemainingCents(p) > 0;

const refundErrorMessage = (err: unknown): string => {
  const raw = String((err as any)?.message || 'The refund could not be completed. Please try again.');
  return raw.replace(/^Firebase:\s*/i, '').replace(/\s*\(functions\/[a-z-]+\)\.?$/i, '');
};

const teacherEditableFields = [
  'arrivalProcedure',
  'dismissalProcedure',
  'lateArrivalProcedure',
  'bathroomRules',
  'hallwayRules',
  'parkingInstructions',
  'classroomName',
  'wifiUsername',
  'wifiPassword',
  'hasProjectorOrTv',
  'projectorConnectionInstructions',
  'additionalNotes',
] as const;

const titleCase = (s: string) =>
  s
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(w => w.slice(0, 1).toUpperCase() + w.slice(1))
    .join(' ');

const formatYmdShort = (raw?: string) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  // Preferred storage is YYYY-MM-DD.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) {
    const yy = m[1].slice(2);
    return `${m[2]}/${m[3]}/${yy}`;
  }
  // Best-effort fallback for legacy strings.
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(2);
  return `${mm}/${dd}/${yy}`;
};

const formatMaybeDate = (d?: string) => (d ? (formatYmdShort(d) || d) : '—');
const normalizeDateList = (raw: any): string[] => {
  if (!Array.isArray(raw)) return [];
  const cleaned = raw
    .map((x) => String(x || '').trim())
    .filter(Boolean);
  // Preserve legacy date-like strings, but de-dupe + stable sort for YYYY-MM-DD.
  const unique = Array.from(new Set(cleaned));
  unique.sort();
  return unique;
};
const formatDateList = (raw: any) => {
  const list = normalizeDateList(raw);
  if (list.length === 0) return '—';
  const formatted = list.map(d => formatYmdShort(d) || d);
  return formatted.join(', ');
};
const DAY_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'MON', label: 'Monday' },
  { id: 'TUE', label: 'Tuesday' },
  { id: 'WED', label: 'Wednesday' },
  { id: 'THU', label: 'Thursday' },
  { id: 'FRI', label: 'Friday' },
  { id: 'SAT', label: 'Saturday' },
  { id: 'SUN', label: 'Sunday' },
];

const CONTACT_ROLE_OPTIONS: Array<{ id: ContactRole; label: string }> = [
  { id: 'primary', label: 'Main POC' },
  { id: 'principal', label: 'Principal / CEO' },
  { id: 'pta_president', label: 'PTO / PTA' },
  { id: 'marketing_director', label: 'Marketing' },
  { id: 'afterschool_director', label: 'Afterschool director' },
  { id: 'finance', label: 'Finance' },
  { id: 'operations', label: 'Operations / secretary' },
  { id: 'other', label: 'Other' },
];

const formatCentsUsd = (cents?: number | null) => {
  if (cents === undefined || cents === null || Number.isNaN(Number(cents))) return '—';
  return `$${(Number(cents) / 100).toFixed(2)}`;
};

const formatDeliveryMode = (raw?: any) => {
  const v = String(raw || '').trim();
  if (!v) return '—';
  if (v === 'in_person') return 'In-person';
  if (v === 'online') return 'Online';
  return v;
};

const formatTime12h = (raw?: any): string => {
  const s = String(raw || '').trim();
  if (!s) return '—';
  const m = /^(\d{1,2}):(\d{2})/.exec(s);
  if (!m) return s;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return s;
  const h = ((hh % 12) === 0) ? 12 : (hh % 12);
  return `${h}:${String(mm).padStart(2, '0')}`;
};

const parseUsdToCents = (value: string): number | undefined => {
  const raw = String(value || '').trim();
  if (!raw) return undefined;
  const n = Number(raw.replace(/[^0-9.\-]/g, ''));
  if (Number.isNaN(n)) return undefined;
  return Math.round(n * 100);
};

// Payroll admin-pay rates are stored in dollars (matching User.hourlyRate), not cents.
const parseUsdToDollars = (value: string): number | null => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const n = Number(raw.replace(/[^0-9.\-]/g, ''));
  if (Number.isNaN(n)) return null;
  return Math.round(n * 100) / 100;
};

const formatHourlyUsd = (rate?: number | null): string => {
  if (rate === undefined || rate === null || Number.isNaN(Number(rate))) return '—';
  return `$${Number(rate).toFixed(2)}/hr`;
};

const centsToEditableUsd = (cents?: number | null): string => {
  if (cents === undefined || cents === null || Number.isNaN(Number(cents))) return '';
  return (Number(cents) / 100).toFixed(2);
};

const isEmptyValue = (v: any, opts?: { treatZeroAsEmpty?: boolean }) => {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return true;
    if (opts?.treatZeroAsEmpty && v === 0) return true;
  }
  return false;
};

const SquadDetailsPage: React.FC<Props> = ({ mode }) => {
  const navigate = useNavigate();
  const { squadId } = useParams<{ squadId: string }>();
  const { currentUser, userData } = useAuth();

  const isAdmin = (userData as any)?.isAdmin === true;
  // If the user is an admin, always unlock admin editability (even if they visit the teacher route).
  const effectiveMode: Mode = isAdmin ? 'admin' : mode;

  const [squad, setSquad] = useState<Squad | null>(null);
  const sessionPlanEditedRef = useRef(false);
  const sessionPlanBaselineRef = useRef<Squad | null>(null);
  const [draft, setDraft] = useState<Partial<Squad>>({});
  const [school, setSchool] = useState<School | null>(null);
  const [contacts, setContacts] = useState<PartnerContact[]>([]);
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [contactDraft, setContactDraft] = useState<{ displayName: string; email: string; phone: string; role: ContactRole }>({ displayName: '', email: '', phone: '', role: 'primary' });
  const [contactSaving, setContactSaving] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [teacherDirectory, setTeacherDirectory] = useState<User[]>([]);
  const [students, setStudents] = useState<User[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [newStudentName, setNewStudentName] = useState('');
  const [editingStudentId, setEditingStudentId] = useState<string | null>(null);
  const [studentNameDraft, setStudentNameDraft] = useState('');
  const [studentActionId, setStudentActionId] = useState<string | null>(null);
  const [rosterError, setRosterError] = useState<string | null>(null);
  const [userNameById, setUserNameById] = useState<Record<string, string>>({});
  const [allSchools, setAllSchools] = useState<Array<{ id: string; name: string; address?: string }>>([]);
  const [partnerDefaultPaymentMode, setPartnerDefaultPaymentMode] = useState<string>('');
  const [partnerDefaultProgramPriceCents, setPartnerDefaultProgramPriceCents] = useState<number | null>(null);
  const [partnerTimezone, setPartnerTimezone] = useState<string>('');
  const [demoExperiences, setDemoExperiences] = useState<ManagedCurriculum[]>([]);
  const [uploadingDemoFlyer, setUploadingDemoFlyer] = useState(false);
  const [draggingDemoFlyer, setDraggingDemoFlyer] = useState(false);
  const [billingClaims, setBillingClaims] = useState<Array<{ id: string; classDate: string; state: 'draft' | 'sent'; invoiceId: string }>>([]);

  const [payments, setPayments] = useState<SquadPayment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentPeople, setPaymentPeople] = useState<Record<string, { name: string; email?: string }>>({});
  const [refundTarget, setRefundTarget] = useState<SquadPayment | null>(null);
  const [refundAmountInput, setRefundAmountInput] = useState('');
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [refundSuccess, setRefundSuccess] = useState<{ amountCents: number; email?: string } | null>(null);
  const [bulkRefundList, setBulkRefundList] = useState<SquadPayment[]>([]);
  const [bulkRefundOpen, setBulkRefundOpen] = useState(false);
  const [bulkRefundRunning, setBulkRefundRunning] = useState(false);
  const [bulkRefundStarted, setBulkRefundStarted] = useState(false);
  const [bulkRefundProgress, setBulkRefundProgress] = useState<Record<string, { state: 'running' | 'done' | 'error'; message?: string }>>({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [priceInputByField, setPriceInputByField] = useState<Record<string, string>>({});
  const [leadTeacherQuery, setLeadTeacherQuery] = useState<string>('');
  const [coTeacherQuery, setCoTeacherQuery] = useState<string>('');
  const [coTeacherRateInputs, setCoTeacherRateInputs] = useState<Record<string, string>>({});
  const [leadRateInput, setLeadRateInput] = useState<string | null>(null);
  const [staffRateInputs, setStaffRateInputs] = useState<Record<string, string>>({});
  const [staffMemberQuery, setStaffMemberQuery] = useState('');
  const [staffRoleToAdd, setStaffRoleToAdd] = useState<SquadStaffRole>('viewer');
  const [upcomingCoverage, setUpcomingCoverage] = useState<Array<{
    id: string;
    start: Date;
    role: 'lead' | 'co_teacher';
    requestedTeacherId: string;
    substituteTeacherId?: string;
    status: 'open' | 'claimed';
  }>>([]);
  const confirmResolverRef = useRef<null | ((v: boolean) => void)>(null);
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    message: string;
    confirmText: string;
    cancelText?: string;
  }>({ open: false, title: '', message: '', confirmText: 'Confirm', cancelText: 'Cancel' });

  const autosaveTimerRef = useRef<number | null>(null);
  const dirtyRef = useRef(false);
  const draftRef = useRef<Partial<Squad>>({});
  const draftRevisionRef = useRef(0);
  const lastSavedRevisionRef = useRef(0);

  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (editingKey === 'regularTeacher') {
      setLeadTeacherQuery('');
    }
  }, [editingKey]);

  useEffect(() => {
    if (editingKey === 'tuitionPerStudentPerClassCents') {
      const cents = (draft as any).tuitionPerStudentPerClassCents ?? (squad as any)?.tuitionPerStudentPerClassCents ?? partnerDefaultProgramPriceCents;
      setPriceInputByField(prev => ({
        ...prev,
        tuitionPerStudentPerClassCents: centsToEditableUsd(cents),
      }));
      return;
    }
    if (editingKey === 'flatFeePerClassCents') {
      const cents = (draft as any).flatFeePerClassCents ?? (squad as any)?.flatFeePerClassCents ?? partnerDefaultProgramPriceCents;
      setPriceInputByField(prev => ({
        ...prev,
        flatFeePerClassCents: centsToEditableUsd(cents),
      }));
      return;
    }
  }, [editingKey, partnerDefaultProgramPriceCents]);

  const curriculumOptions = useMemo(() => {
    const d: any = (OPS_LOGISTICS_BY_ID as any)['squad.curriculumId'];
    const opts = Array.isArray(d?.enumOptions) ? (d.enumOptions as Array<{ id: string; label: string }>) : [];
    const labels = new Map(scaffoldedCurricula.map((curriculum) => [curriculum.id, curriculum.title]));
    const merged = new Map(opts.map((option) => [option.id, option]));
    listCurriculumAdapters().forEach((adapter) => {
      if (!merged.has(adapter.id)) {
        merged.set(adapter.id, {
          id: adapter.id,
          label: labels.get(adapter.id) || titleCase(adapter.id.replace(/-/g, ' ')),
        });
      }
    });
    return Array.from(merged.values());
  }, []);

  const timezoneOptions = useMemo(() => {
    const d: any = (OPS_LOGISTICS_BY_ID as any)['squad.timezone'];
    const opts = Array.isArray(d?.enumOptions) ? (d.enumOptions as Array<{ id: string; label: string }>) : [];
    return opts;
  }, []);

  const gradeStartOptions = useMemo(() => {
    const d: any = (OPS_LOGISTICS_BY_ID as any)['squad.startGrade'];
    const opts = Array.isArray(d?.enumOptions) ? (d.enumOptions as Array<{ id: string; label: string }>) : [];
    return opts;
  }, []);

  const gradeEndOptions = useMemo(() => {
    const d: any = (OPS_LOGISTICS_BY_ID as any)['squad.endGrade'];
    const opts = Array.isArray(d?.enumOptions) ? (d.enumOptions as Array<{ id: string; label: string }>) : [];
    return opts;
  }, []);

  const teacherSelectOptions = useMemo(() => {
    return teacherDirectory.map(t => ({
      id: String(t.id),
      label: String(t.displayName || t.email || t.id),
    }));
  }, [teacherDirectory]);

  const ensureCurrentOption = (options: Array<{ id: string; label: string }>, currentId: string) => {
    const cid = String(currentId || '').trim();
    if (!cid) return options;
    if (options.some(o => String(o.id) === cid)) return options;
    return [{ id: cid, label: `${cid} (legacy)` }, ...options];
  };

  const canViewAsTeacher = useMemo(() => {
    if (!squad || !currentUser) return false;
    return canAccessSquad(userData, squad);
  }, [squad, currentUser, userData]);

  useEffect(() => {
    let cancelled = false;
    const ids = Array.from(new Set((squad?.studentIds || []).map(id => String(id || '').trim()).filter(Boolean)));
    if (ids.length === 0) {
      setStudents([]);
      setStudentsLoading(false);
      return () => { cancelled = true; };
    }

    setStudentsLoading(true);
    Promise.all(ids.map(id => getDoc(doc(db, 'users', id))))
      .then((snapshots) => {
        if (cancelled) return;
        const byId = new Map(
          snapshots
            .filter(snapshot => snapshot.exists())
            .map(snapshot => [snapshot.id, { ...(snapshot.data() as any), id: snapshot.id } as User]),
        );
        setStudents(ids.map(id => byId.get(id)).filter((student): student is User => Boolean(student)));
      })
      .catch((loadError) => {
        console.error('Failed to load squad students', loadError);
        if (!cancelled) setRosterError('Could not load the class roster.');
      })
      .finally(() => {
        if (!cancelled) setStudentsLoading(false);
      });

    return () => { cancelled = true; };
  }, [squad?.studentIds]);

  // Stripe payments for this squad (admin only). Live so webhook reconciliation
  // and refunds issued elsewhere show up without a reload.
  useEffect(() => {
    if (effectiveMode !== 'admin' || !squadId) return undefined;
    setPaymentsLoading(true);
    const unsubscribe = onSnapshot(
      query(collection(db, 'payments'), where('squadId', '==', squadId)),
      (snap) => {
        const rows = snap.docs.map((d) => ({ ...(d.data() as any), id: d.id })) as SquadPayment[];
        rows.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
        setPayments(rows);
        setPaymentsLoading(false);
      },
      (paymentsError) => {
        console.error('Failed to load squad payments', paymentsError);
        setPaymentsLoading(false);
      },
    );
    return () => unsubscribe();
  }, [effectiveMode, squadId]);

  // Resolve student names + parent emails for the payment rows.
  useEffect(() => {
    const wanted = Array.from(new Set(
      payments
        .flatMap((p) => [p.studentId, p.parentId])
        .map((id) => String(id || '').trim())
        .filter(Boolean),
    ));
    const missing = wanted.filter((id) => !paymentPeople[id]);
    if (missing.length === 0) return undefined;
    let cancelled = false;
    Promise.all(missing.map((id) => getDoc(doc(db, 'users', id)).catch(() => null)))
      .then((snaps) => {
        if (cancelled) return;
        setPaymentPeople((prev) => {
          const next = { ...prev };
          snaps.forEach((snap, i) => {
            const data = snap && snap.exists() ? (snap.data() as any) : null;
            next[missing[i]] = {
              name: String(data?.displayName || '').trim() || 'Unknown',
              email: String(data?.email || '').trim() || undefined,
            };
          });
          return next;
        });
      });
    return () => { cancelled = true; };
  }, [payments, paymentPeople]);

  // Upcoming substitute coverage for this squad (admin, read-only summary).
  useEffect(() => {
    if (effectiveMode !== 'admin' || !squadId) return undefined;
    let cancelled = false;
    getDocs(query(collection(db, 'squadEvents'), where('squadId', '==', squadId)))
      .then((snap) => {
        if (cancelled) return;
        const cutoff = Date.now() - 24 * 60 * 60 * 1000;
        const rows = snap.docs
          .map((d) => {
            const data: any = d.data();
            const coverage = Array.isArray(data.activeCoverage) && data.activeCoverage.length > 0 ? data.activeCoverage[0] : null;
            if (!coverage) return null;
            const start: Date | null = data.start?.toDate?.() || null;
            if (!start || start.getTime() < cutoff) return null;
            return {
              id: d.id,
              start,
              role: (coverage.requestedRole === 'co_teacher' ? 'co_teacher' : 'lead') as 'lead' | 'co_teacher',
              requestedTeacherId: String(coverage.requestedTeacherId || ''),
              substituteTeacherId: String(coverage.substituteTeacherId || '') || undefined,
              status: (coverage.status === 'claimed' ? 'claimed' : 'open') as 'open' | 'claimed',
            };
          })
          .filter((row): row is NonNullable<typeof row> => Boolean(row))
          .sort((a, b) => a.start.getTime() - b.start.getTime());
        setUpcomingCoverage(rows);
      })
      .catch((coverageError) => console.warn('Failed to load squad coverage summary', coverageError));
    return () => { cancelled = true; };
  }, [effectiveMode, squadId]);

  const refundablePayments = useMemo(() => payments.filter(isRefundablePayment), [payments]);
  const refundableTotalCents = useMemo(
    () => refundablePayments.reduce((sum, p) => sum + paymentRemainingCents(p), 0),
    [refundablePayments],
  );
  const squadIsCanceled = ['canceled', 'cancelled'].includes(
    String(draft.status || squad?.status || '').trim().toLowerCase(),
  );

  const paymentStudentName = (p: SquadPayment) =>
    paymentPeople[String(p.studentId || '')]?.name || 'Unknown student';
  const paymentParentLabel = (p: SquadPayment) => {
    const parent = paymentPeople[String(p.parentId || '')];
    return parent?.email || parent?.name || 'no parent email on file';
  };

  const executeStripeRefund = async (payment: SquadPayment, amountCents?: number) => {
    const call = httpsCallable(getFunctions(), 'refundStripePayment');
    const result: any = await call({
      paymentId: payment.id,
      ...(amountCents != null ? { amountCents } : {}),
    });
    return result?.data as { amountCents: number; refundStatus: string; emailQueued: boolean };
  };

  const openRefundModal = (payment: SquadPayment) => {
    setRefundTarget(payment);
    setRefundAmountInput((paymentRemainingCents(payment) / 100).toFixed(2));
    setRefundError(null);
    setRefundSuccess(null);
  };

  const closeRefundModal = () => {
    setRefundTarget(null);
    setRefundError(null);
    setRefundSuccess(null);
    setRefundBusy(false);
  };

  const refundAmountCentsParsed = useMemo(() => {
    const parsed = Number.parseFloat(refundAmountInput.replace(/[$,\s]/g, ''));
    if (!Number.isFinite(parsed)) return null;
    return Math.round(parsed * 100);
  }, [refundAmountInput]);

  const submitRefund = async () => {
    if (!refundTarget || refundBusy) return;
    const remaining = paymentRemainingCents(refundTarget);
    const amount = refundAmountCentsParsed;
    if (amount == null || amount <= 0) {
      setRefundError('Enter a refund amount greater than $0.00.');
      return;
    }
    if (amount > remaining) {
      setRefundError(`The most you can refund is ${fmtUsdFromCents(remaining)}.`);
      return;
    }
    setRefundBusy(true);
    setRefundError(null);
    try {
      await executeStripeRefund(refundTarget, amount);
      setRefundSuccess({
        amountCents: amount,
        email: paymentPeople[String(refundTarget.parentId || '')]?.email,
      });
    } catch (err) {
      setRefundError(refundErrorMessage(err));
    } finally {
      setRefundBusy(false);
    }
  };

  const openBulkRefund = () => {
    setBulkRefundList(refundablePayments);
    setBulkRefundProgress({});
    setBulkRefundStarted(false);
    setBulkRefundOpen(true);
  };

  const closeBulkRefund = () => {
    if (bulkRefundRunning) return;
    setBulkRefundOpen(false);
    setBulkRefundList([]);
    setBulkRefundProgress({});
    setBulkRefundStarted(false);
  };

  const runBulkRefund = async () => {
    if (bulkRefundRunning) return;
    setBulkRefundRunning(true);
    setBulkRefundStarted(true);
    for (const payment of bulkRefundList) {
      setBulkRefundProgress((prev) => ({ ...prev, [payment.id]: { state: 'running' } }));
      try {
        await executeStripeRefund(payment);
        setBulkRefundProgress((prev) => ({ ...prev, [payment.id]: { state: 'done' } }));
      } catch (err) {
        setBulkRefundProgress((prev) => ({
          ...prev,
          [payment.id]: { state: 'error', message: refundErrorMessage(err) },
        }));
      }
    }
    setBulkRefundRunning(false);
  };

  const schoolId = useMemo(() => String((draft.locationId ?? squad?.locationId) || '').trim(), [draft.locationId, squad?.locationId]);
  const partnerId = useMemo(() => String((draft.partnerId ?? squad?.partnerId) || '').trim(), [draft.partnerId, squad?.partnerId]);
  const assignedLocationTitle = useMemo(
    () => String(school?.name || draft.locationName || 'Assigned location').trim(),
    [school?.name, draft.locationName]
  );

  const backTarget = useMemo(() => {
    if (effectiveMode === 'admin') return '/app/admin';
    // Prefer going back to the squad dashboard (common teacher flow).
    if (squadId) return `/app/squads/${squadId}`;
    return '/app/squads';
  }, [effectiveMode, squadId]);

  useEffect(() => {
    if (!squadId) {
      setError('No squad id provided.');
      setLoading(false);
      return;
    }

    setLoading(true);
    const ref = doc(db, 'squads', squadId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setSquad(null);
          setError('Squad not found.');
          setLoading(false);
          return;
        }
        const next = { ...(snap.data() as any), id: snap.id } as Squad;
        setSquad(next);
        // Only overwrite the local draft if the user hasn't started editing.
        setDraft(prev => (dirtyRef.current ? prev : next));
        setError(null);
        setLoading(false);
      },
      (e) => {
        console.error('Failed to subscribe to squad', e);
        setError('Failed to load squad.');
        setLoading(false);
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [squadId]);

  useEffect(() => {
    if (!squadId || effectiveMode !== 'admin') { setBillingClaims([]); return; }
    const claimsQuery = query(collection(db, 'invoiceSessionClaims'), where('squadId', '==', squadId));
    return onSnapshot(claimsQuery, (snapshot) => setBillingClaims(snapshot.docs.map((item) => ({ id: item.id, ...(item.data() as any) })).filter((item: any) => !item.voidedAt) as any), () => setBillingClaims([]));
  }, [effectiveMode, squadId]);

  const scheduledBillingDates = useMemo(() => {
    const source: any = draft.schedule || squad?.schedule || {}; const single = String(source.mainDate || '').slice(0, 10); if (single) return [single];
    const start = new Date(`${String(source.startDate || '').slice(0, 10)}T00:00:00Z`); const end = new Date(`${String(source.endDate || '').slice(0, 10)}T00:00:00Z`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
    const dow: Record<string, number> = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 }; const target = dow[String(source.dayOfWeek || '').slice(0, 3).toUpperCase()]; if (target == null) return [];
    const omitted = new Set([...(source.exceptions || []), ...(source.inheritedExceptions || []), ...(source.canceledDates || []), ...((draft as any).canceledClassDates || [])].map(String)); const dates: string[] = [];
    for (let cursor = new Date(start); cursor <= end; cursor.setUTCDate(cursor.getUTCDate() + 1)) { const date = cursor.toISOString().slice(0, 10); if (cursor.getUTCDay() === target && !omitted.has(date)) dates.push(date); }
    [...(source.makeups || []), ...((draft as any).restoredClassDates || [])].map(String).forEach((date) => { if (date && !omitted.has(date)) dates.push(date); }); return Array.from(new Set(dates)).sort();
  }, [draft, squad]);

  // Load school + contacts (teacher-visible) once schoolId is known.
  useEffect(() => {
    if (!schoolId) {
      setSchool(null);
      setContacts([]);
      return;
    }

    // Canonical location is the active source for shared logistics.
    const schoolRef = doc(db, 'locations', schoolId);
    const unsub = onSnapshot(
      schoolRef,
      (snap) => {
        const nextSchool = snap.exists() ? ({ id: schoolId, ...(snap.data() as any) } as School) : null;
        setSchool(nextSchool);

        // Location is the SSOT for shared teaching logistics in both admin and
        // teacher views. Legacy squad copies remain a temporary fallback only.
        if (nextSchool && !dirtyRef.current) {
          setDraft((prev: any) => {
            const next: any = { ...(prev || {}) };
            for (const key of LOCATION_TEACHING_LOGISTICS_FIELDS) {
              const schoolVal = (nextSchool as any)?.[key];
              const hasSchoolVal =
                schoolVal !== undefined
                && schoolVal !== null
                && !(typeof schoolVal === 'string' && schoolVal.trim() === '');
              // Prefer school value when present; otherwise keep current value (legacy squad copy).
              if (hasSchoolVal) next[key] = schoolVal;
            }
            const locationUnknown = (nextSchool as any)?.teacherLogisticsUnknown || {};
            next.teacherLogisticsUnknown = {
              ...((prev as any)?.teacherLogisticsUnknown || {}),
              ...Object.fromEntries(
                LOCATION_TEACHING_LOGISTICS_FIELDS
                  .map((key) => [key, locationUnknown?.[key] === true])
              ),
            };
            return next;
          });
        }
      },
      (e) => {
        console.warn('Failed to subscribe to school doc', e);
        setSchool(null);
      }
    );

    // Contacts can stay as a one-time fetch; they rarely change mid-edit.
    const loadContacts = async () => {
      try {
        const partnerId = String((draft.partnerId ?? squad?.partnerId) || '').trim();
        if (!partnerId) return;
        const snap = await getDocs(collection(db, 'partners', partnerId, 'contacts'));
        const list = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as PartnerContact[];
        list.sort((a, b) => String(a.displayName || '').toLowerCase().localeCompare(String(b.displayName || '').toLowerCase()));
        setContacts(list);
      } catch (e) {
        console.warn('Failed to load contacts for school', e);
        setContacts([]);
      }
    };
    void loadContacts();

    return () => unsub();
  }, [effectiveMode, schoolId]);

  // Load schools list for admin location picker.
  useEffect(() => {
    const run = async () => {
      if (effectiveMode !== 'admin' || !partnerId) {
        setAllSchools([]);
        return;
      }
      try {
        const snap = await getDocs(query(collection(db, 'locations'), where('partnerId', '==', partnerId)));
        const list = snap.docs
          .map(d => ({
            id: d.id,
            name: String((d.data() as any)?.name || ''),
            address: String((d.data() as any)?.address || ''),
          }))
          .filter(x => x.name.trim() !== '');
        list.sort((a, b) => a.name.localeCompare(b.name));
        setAllSchools(list);
      } catch (e) {
        console.warn('Failed to load partner locations for squad details', e);
        setAllSchools([]);
      }
    };
    void run();
  }, [effectiveMode, partnerId]);

  useEffect(() => {
    if (!partnerId) {
      setPartnerDefaultPaymentMode('');
      setPartnerDefaultProgramPriceCents(null);
      setPartnerTimezone('');
      return;
    }
    const unsub = onSnapshot(doc(db, 'partners', partnerId), (snap) => {
      const defaults = (snap.data() as any)?.billingDefaults || {};
      setPartnerDefaultPaymentMode(String(defaults.paymentMode || '').trim());
      setPartnerTimezone(String((snap.data() as any)?.timezone || '').trim());
      const price = Number(defaults.defaultProgramPriceCents);
      setPartnerDefaultProgramPriceCents(Number.isFinite(price) && price >= 0 ? price : null);
    }, () => {
      setPartnerDefaultPaymentMode('');
      setPartnerDefaultProgramPriceCents(null);
      setPartnerTimezone('');
    });
    return () => unsub();
  }, [partnerId]);

  useEffect(() => {
    listManagedCurricula()
      .then((items) => setDemoExperiences(items.filter((item) => item.kind === 'demo_experience' && item.published !== false)))
      .catch(() => setDemoExperiences([]));
  }, []);

  const changeSquadPaymentMode = async (nextMode: string) => {
    const currentMode = String(draft.paymentMode || 'stripe');
    if (!nextMode || nextMode === currentMode) {
      setEditingKey(null);
      return;
    }
    if (partnerId && nextMode !== partnerDefaultPaymentMode) {
      const updateDefault = await promptConfirm({
        title: 'Use this payment mode going forward?',
        message: `${partnerDefaultPaymentMode ? `This partner's default is ${titleCase(partnerDefaultPaymentMode)}.` : 'This partner does not have a default squad payment mode yet.'}\n\nChoose “Update partner default” to use ${titleCase(nextMode)} for this squad and future squads. Choose “This squad only” to leave the partner default unchanged.`,
        confirmText: 'Update partner default',
        cancelText: 'This squad only',
      });
      if (updateDefault) {
        await updateDoc(doc(db, 'partners', partnerId), {
          'billingDefaults.paymentMode': nextMode,
          updatedAt: Date.now(),
          updatedBy: String((userData as any)?.id || currentUser?.uid || '').trim() || null,
        } as any);
        setPartnerDefaultPaymentMode(nextMode);
      }
    }
    updateDraft({ paymentMode: nextMode as any } as any);
    setEditingKey(null);
  };

  // Load minimal user display names for teacher-visible labels and "last updated" lines.
  useEffect(() => {
    const ids = new Set<string>();
    const a = String((squad as any)?.detailsUpdatedBy || '').trim();
    const b = String((squad as any)?.teacherDetailsUpdatedBy || '').trim();
    const leadTeacherId = String((squad as any)?.regularTeacher || (squad as any)?.leadTeacherId || '').trim();
    if (a) ids.add(a);
    if (b) ids.add(b);
    if (leadTeacherId) ids.add(leadTeacherId);
    const list = Array.from(ids);
    if (list.length === 0) return;

    const run = async () => {
      const out: Record<string, string> = {};
      await Promise.all(
        list.map(async (id) => {
          try {
            const snap = await getDoc(doc(db, 'users', id));
            if (snap.exists()) {
              const d: any = snap.data();
              out[id] = String(d.displayName || d.email || id);
            }
          } catch (_) {
            // ignore
          }
        })
      );
      setUserNameById(prev => ({ ...prev, ...out }));
    };
    void run();
  }, [squad]);

  // Load teacher directory (admin only; used for contact owner + nicer labels).
  useEffect(() => {
    const run = async () => {
      if (effectiveMode !== 'admin') {
        setTeacherDirectory([]);
        return;
      }
      try {
        const q = query(collection(db, 'users'), where('role', '==', 'teacher'));
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ ...(d.data() as any), id: d.id } as User));
        list.sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || '')));
        setTeacherDirectory(list);
      } catch (e) {
        console.warn('Failed to load teacher directory for admin details page', e);
        setTeacherDirectory([]);
      }
    };
    void run();
  }, [effectiveMode]);

  const sanitizeForFirestore = (value: any): any => {
    if (value === undefined) return undefined;
    if (Array.isArray(value)) {
      const arr = value.map(sanitizeForFirestore).filter(v => v !== undefined);
      return arr;
    }
    if (value && typeof value === 'object') {
      const out: any = {};
      Object.entries(value).forEach(([k, v]) => {
        const cleaned = sanitizeForFirestore(v);
        if (cleaned === undefined) return;
        if (cleaned && typeof cleaned === 'object' && !Array.isArray(cleaned) && Object.keys(cleaned).length === 0) return;
        out[k] = cleaned;
      });
      return out;
    }
    return value;
  };

  const buildUpdatePayload = (source: Partial<Squad>): Record<string, any> => {
    const now = Date.now();
    const uid = String((userData as any)?.id || currentUser?.uid || '').trim();

    const base: any = {};

    // Teacher mode: ONLY write teacher-editable fields.
    if (effectiveMode === 'teacher') {
      teacherEditableFields
        .filter((key) => !LOCATION_TEACHING_LOGISTICS_FIELDS.includes(key as any))
        .forEach((key) => {
        base[key] = (source as any)[key];
      });
      base.detailsUpdatedAt = now;
      base.detailsUpdatedBy = uid || null;
      base.teacherDetailsUpdatedAt = now;
      base.teacherDetailsUpdatedBy = uid || null;
      return sanitizeForFirestore(base);
    }

    // Admin mode: write everything in draft except immutable/system fields.
    Object.assign(base, source);
    if (Object.prototype.hasOwnProperty.call(base, 'regularTeacher')) {
      // Permanent compatibility mirror: every lead edit writes both fields.
      base.leadTeacherId = base.regularTeacher || null;
    }
    delete base.id;
    delete base.teacherIds;
    delete base.studentIds;
    LOCATION_TEACHING_LOGISTICS_FIELDS.forEach((key) => delete base[key]);
    base.detailsUpdatedAt = now;
    base.detailsUpdatedBy = uid || null;
    return sanitizeForFirestore(base);
  };

  const save = async () => {
    if (!squadId) return;
    const draftToSave = (draftRef.current || {}) as Partial<Squad>;
    if (!draftToSave) return;
    if (effectiveMode === 'teacher' && !canViewAsTeacher) {
      setError('You do not have permission to view this squad.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saveRevision = draftRevisionRef.current;
      const oldRegularTeacher = (squad as any)?.regularTeacher ? String((squad as any).regularTeacher) : null;
      const incomingRegularTeacher = (draftToSave as any)?.regularTeacher ? String((draftToSave as any).regularTeacher) : null;
      const leadTeacherChanged =
        effectiveMode === 'admin'
        && incomingRegularTeacher !== oldRegularTeacher;
      // A lead teacher selected in Squad Details is the squad-wide assignment.
      // Individual event staffing can still be edited afterward, but an explicit
      // squad-level lead change must update the complete class series.
      const overwriteAllLeadEvents = leadTeacherChanged;

      const normalizeCoTeacherIds = (list: any): string[] => Array.from(new Set(
        (Array.isArray(list) ? list : [])
          .map((c: any) => String(c?.teacherId || '').trim())
          .filter(Boolean)
      ));
      const oldCoTeacherIds = normalizeCoTeacherIds((squad as any)?.coTeachers);
      const newCoTeacherIds = normalizeCoTeacherIds(
        (draftToSave as any)?.coTeachers !== undefined ? (draftToSave as any).coTeachers : (squad as any)?.coTeachers
      );
      const coTeachersChanged =
        effectiveMode === 'admin'
        && (oldCoTeacherIds.length !== newCoTeacherIds.length
          || newCoTeacherIds.some(id => !oldCoTeacherIds.includes(id)));

      // If lead teacher is being changed, run conflict checks before saving.
      let leadAnalysis: Awaited<ReturnType<typeof analyzeLeadTeacherAssignment>> | null = null;
      if (leadTeacherChanged && incomingRegularTeacher && squad) {
        leadAnalysis = await analyzeLeadTeacherAssignment({
          squad,
          newTeacherId: incomingRegularTeacher,
          overwriteAllEvents: overwriteAllLeadEvents,
        });

        if (!leadAnalysis.hasAnyWeeklyAvailabilityTemplate) {
          const ok = await promptConfirm({
            title: 'Teacher has no availability schedule',
            message:
              'This teacher has not entered any weekly availability yet.\n\n'
              + 'Assign them as lead teacher anyway?',
            confirmText: 'Assign anyway',
          });
          if (!ok) {
            setSaving(false);
            return;
          }
        }

      }

      const planEdited = sessionPlanEditedRef.current;
      const planBaseline = sessionPlanBaselineRef.current;
      if (planEdited && draftToSave.curriculumId !== squad?.curriculumId) {
        throw new Error('Save a whole-class curriculum change separately from session assignments.');
      }
      const payload = buildUpdatePayload(draftToSave);
      if (!planEdited) delete payload.sessionPlan;
      // Progress is owned by the classroom, never by the details draft.
      ['completedLevels', 'missionProgress', 'currentLessonId', 'classStatus', 'liveSession', 'attendance', 'attendanceMetadata', 'lastAttendanceUpdate', 'unlockedGames', 'activeLaunchSession'].forEach(key => delete payload[key]);
      if (
        effectiveMode === 'admin'
        && (String((draftToSave as any).scheduleKind || (squad as any)?.scheduleKind) === 'single_day'
          || Boolean((draftToSave as any).schedule?.mainDate))
      ) {
        payload.schedule = normalizedSingleDaySchedule((draftToSave as any).schedule);
        payload.scheduleKind = 'single_day';
        payload.totalClasses = 1;
      }

      if (effectiveMode === 'admin') {
        const partnerId = String((draftToSave as any).partnerId || (squad as any)?.partnerId || '').trim();
        const firstClassDate = String((draftToSave as any).schedule?.mainDate || (draftToSave as any).schedule?.startDate || '').slice(0, 10);
        if (partnerId && firstClassDate) {
          const partnerSeasons = await listPartnerSeasons(partnerId);
          const suggestion = inferSeasonFromStartDate(firstClassDate, partnerSeasons);
          const currentTermId = String((draftToSave as any).termId || (draftToSave as any).seasonId || '').trim();
          let acceptSuggestion = Boolean(suggestion && !currentTermId);
          if (suggestion && currentTermId && suggestion.termId !== currentTermId) {
            acceptSuggestion = await promptConfirm({
              title: 'Move squad to another season?',
              message: `${suggestion.reason}\n\nThis squad is currently assigned to ${programTermLabelFromId(currentTermId) || currentTermId}. Move it to ${programTermLabelFromId(suggestion.termId) || suggestion.termId}?`,
              confirmText: 'Move season',
            });
          }
          if (suggestion && acceptSuggestion) {
            (payload as any).termId = suggestion.termId;
            (payload as any).seasonId = suggestion.termId;
            (payload as any).term = programTermLabelFromId(suggestion.termId);
            (payload as any).seasonAssignmentSource = 'inferred_from_start_date';
            (payload as any).seasonAssignmentReason = suggestion.reason;
          }
        }
      }

      // Curriculum changes can cause progress collisions because squad-level progress is keyed by levelId
      // (e.g. many curricula use levels "1".."12"). To keep squads consistent, reset level progress
      // when curriculumId changes in Admin mode.
      if (effectiveMode === 'admin' && squad) {
        const prevCurr = String((squad as any)?.curriculumId || 'music').trim().toLowerCase() || 'music';
        const nextCurr = String((draftToSave as any)?.curriculumId || 'music').trim().toLowerCase() || 'music';
        const changed = prevCurr !== nextCurr;

        if (changed) {
          if ((squad as any)?.classStatus === 'current') {
            setError('Cannot change curriculum while a class is live. Finish or cancel the class first.');
            setSaving(false);
            return;
          }

          const ok = await promptConfirm({
            title: 'Change curriculum?',
            message:
              'Changing the curriculum on an existing squad will reset level progress to avoid mixing progress between curricula.\n\n'
              + 'This will reset:\n'
              + '- Current level → 1\n'
              + '- Completed levels → empty\n'
              + '- Mission progress → cleared\n\n'
              + 'Proceed?',
            confirmText: 'Change + Reset Progress',
          });
          if (!ok) {
            setSaving(false);
            return;
          }

          // A full curriculum replacement invalidates any previous session assignments.
          (payload as any).sessionPlan = deleteField();
          (payload as any).currentLessonId = '1';
          (payload as any).completedLevels = [];
          (payload as any).missionProgress = {};
          (payload as any).classStatus = 'next';
        }
      }
      if (!schoolId) throw new Error('This squad does not have a canonical location.');
      const uid = String((userData as any)?.id || currentUser?.uid || '').trim();
      const locationPayload = sanitizeForFirestore({
        ...Object.fromEntries(
          LOCATION_TEACHING_LOGISTICS_FIELDS.map((key) => [key, (draftToSave as any)[key] ?? ''])
        ),
        teacherLogisticsUpdatedAt: Date.now(),
        teacherLogisticsUpdatedBy: uid || null,
        teacherLogisticsLastEditedFromSquadId: squadId,
      });
      if (leadTeacherChanged) {
        delete (payload as any).regularTeacher;
        delete (payload as any).leadTeacherId;
      }
      if (planEdited && draftToSave.sessionPlan) {
        await runTransaction(db, async transaction => {
          const squadRef = doc(db, 'squads', squadId);
          const latest = await transaction.get(squadRef);
          if (!latest.exists()) throw new Error('Squad no longer exists.');
          const latestSquad = latest.data() as Squad;
          assertCurriculumPlanPreservesProgress(latestSquad, draftToSave.sessionPlan!);
          if (hasCurriculumPlanConflict(latestSquad, planBaseline)) {
            throw new Error('The curriculum plan or schedule changed while saving. Reload Squad details and try again.');
          }
          transaction.update(squadRef, payload);
          transaction.update(doc(db, 'locations', schoolId), locationPayload);
        });
        // The assignment is committed even if a later calendar/staff sync fails.
        // A retry must compare against our own successful write.
        sessionPlanBaselineRef.current = {
          ...planBaseline,
          ...draftToSave,
          sessionPlan: draftToSave.sessionPlan,
        } as Squad;
      } else {
        const batch = writeBatch(db);
        batch.update(doc(db, 'squads', squadId), payload);
        batch.update(doc(db, 'locations', schoolId), locationPayload);
        await batch.commit();
      }

      if (leadTeacherChanged || !incomingRegularTeacher) {
        await changeSquadLeadTeacher({
          squadId,
          oldTeacherId: oldRegularTeacher,
          newTeacherId: incomingRegularTeacher,
        });
      }

      // Ensure lead + co-teachers + other paid staff are in teacherIds (arrayUnion only).
      if (effectiveMode === 'admin') {
        const staffPayIds = Object.keys(
          (((draftToSave as any)?.staffPayByTeacherId ?? (squad as any)?.staffPayByTeacherId) || {}) as Record<string, unknown>,
        ).map((id) => String(id).trim());
        const idsToEnsure = Array.from(new Set([incomingRegularTeacher, ...newCoTeacherIds, ...staffPayIds].filter(Boolean))) as string[];
        if (idsToEnsure.length > 0) {
          await updateDoc(doc(db, 'squads', squadId), { teacherIds: arrayUnion(...idsToEnsure) } as any);
        }
      }

      // Calendar + teacher sync after admin save.
      if (effectiveMode === 'admin' && squad) {
        const updated = {
          ...squad,
          ...(draftToSave as any),
          ...((draftToSave as any).regularTeacher !== undefined ? { leadTeacherId: (draftToSave as any).regularTeacher || null } : {}),
        } as Squad;
        await syncSquadCalendarFromDetails(updated);

        if (coTeachersChanged) {
          await syncSquadCoTeachersChange({
            squadId: updated.id,
            timezone: String((updated as any).timezone || DEFAULT_TZ),
            oldCoTeacherIds,
            newCoTeacherIds,
          });
        }
      }

      // Avoid clobbering newer edits that happened while the save was in-flight.
      if (draftRevisionRef.current === saveRevision) {
        lastSavedRevisionRef.current = saveRevision;
        dirtyRef.current = false;
        sessionPlanEditedRef.current = false;
        setDirty(false);
      }
      setSavedAt(Date.now());
    } catch (e) {
      console.error('Failed to save squad details', e);
      const msg = String((e as any)?.message || '');
      setError(msg ? `Failed to save changes. (${msg})` : 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const confirmLeaveWithUnsavedChanges = async (): Promise<boolean> => {
    if (!dirtyRef.current) return true;
    const saveFirst = await window.appConfirm(
      'You have unsaved changes.\n\nPress OK to save before leaving this page.'
    );
    if (saveFirst) {
      await save();
      return !dirtyRef.current;
    }
    return await window.appConfirm('Leave this page without saving your changes?');
  };

  const handleBackClick = async () => {
    const canLeave = await confirmLeaveWithUnsavedChanges();
    if (!canLeave) return;
    if (window.history.length > 1) navigate(-1);
    else navigate(backTarget);
  };

  const scheduleAutosave = () => {
    // Autosave intentionally disabled for teacher edits to reduce accidental destructive saves.
    return;
  };

  const updateDraft = (patch: Partial<Squad>, autosave = false) => {
    draftRevisionRef.current += 1;
    dirtyRef.current = true;
    setDraft(prev => {
      const next = { ...prev, ...patch };
      draftRef.current = next;
      return next;
    });
    setDirty(true);
    if (autosave) scheduleAutosave();
  };

  const uploadDemoFlyer = async (file: File) => {
    if (!squadId || uploadingDemoFlyer) return;
    const allowed = file.type === 'application/pdf' || file.type.startsWith('image/');
    if (!allowed) {
      setError('The demo flyer must be a PDF or image file.');
      return;
    }
    setUploadingDemoFlyer(true);
    setError(null);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, '-');
      const storagePath = `squads/${squadId}/demo-flyers/${Date.now()}-${safeName}`;
      const target = storageRef(storage, storagePath);
      await uploadBytes(target, file, { contentType: file.type || 'application/octet-stream' });
      const url = await getDownloadURL(target);
      updateDraft({ demoFlyer: {
        title: file.name,
        url,
        storagePath,
        uploadedAt: Date.now(),
        uploadedBy: String((userData as any)?.id || currentUser?.uid || '').trim() || undefined,
      } } as Partial<Squad>);
    } catch (uploadError: any) {
      setError(uploadError?.message ? `Could not upload the demo flyer. (${uploadError.message})` : 'Could not upload the demo flyer.');
    } finally {
      setUploadingDemoFlyer(false);
    }
  };

  const updateSchedule = (patch: Record<string, any>) => {
    const merged = { ...(draft.schedule || {}), ...patch };
    const next = Object.prototype.hasOwnProperty.call(patch, 'mainDate')
      ? normalizedSingleDaySchedule(merged)
      : merged;
    updateDraft({ schedule: next } as any);
  };

  const DateListEditor = ({
    kind,
    value,
    onChange,
    placeholder,
  }: {
    kind: 'exceptions' | 'makeups';
    value: any;
    onChange: (next: string[]) => void;
    placeholder?: string;
  }) => {
    const list = normalizeDateList(value);
    const [pending, setPending] = useState('');

    const setAtIndex = (idx: number, nextVal: string) => {
      const arr = [...list];
      arr[idx] = String(nextVal || '').trim();
      const next = normalizeDateList(arr);
      onChange(next);
    };

    const removeAt = (idx: number) => {
      const next = list.filter((_, i) => i !== idx);
      onChange(next);
    };

    const addPending = () => {
      const v = String(pending || '').trim();
      if (!v) return;
      const next = normalizeDateList([...list, v]);
      onChange(next);
      setPending('');
    };

    return (
      <div
        className="squad-details-date-list"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        {list.length === 0 ? <div className="squad-details-date-list-empty">{placeholder || 'None yet.'}</div> : null}
        {list.map((d, idx) => (
          <div key={`${kind}-${d}-${idx}`} className="squad-details-date-list-row">
            <input
              className="squad-details-inline-input squad-details-date-list-input"
              type="date"
              value={String(d || '')}
              onChange={(e) => setAtIndex(idx, e.target.value)}
            />
            <button
              type="button"
              className="squad-details-icon-button"
              onClick={() => removeAt(idx)}
              aria-label="Remove date"
              title="Remove"
            >
              ×
            </button>
          </div>
        ))}
        <div className="squad-details-date-list-row">
          <input
            className="squad-details-inline-input squad-details-date-list-input"
            type="date"
            value={pending}
            onChange={(e) => setPending(e.target.value)}
          />
          <button
            type="button"
            className="squad-details-icon-button"
            onClick={addPending}
            disabled={!pending.trim()}
            aria-label="Add date"
            title="Add"
          >
            +
          </button>
        </div>
      </div>
    );
  };

  const promptConfirm = (opts: { title: string; message: string; confirmText: string; cancelText?: string }): Promise<boolean> => {
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve;
      setConfirmState({ open: true, ...opts });
    });
  };

  const addStudent = async (event: React.FormEvent) => {
    event.preventDefault();
    const displayName = newStudentName.trim();
    if (!squadId || !displayName || studentActionId) return;
    setStudentActionId('new');
    setRosterError(null);
    try {
      const studentRef = doc(collection(db, 'users'));
      const batch = writeBatch(db);
      batch.set(studentRef, {
        id: studentRef.id,
        displayName,
        role: 'student',
        isAdmin: false,
        ogLevel: 1,
        squadIds: [squadId],
        museCash: 50,
        createdAt: serverTimestamp(),
      });
      batch.update(doc(db, 'squads', squadId), { studentIds: arrayUnion(studentRef.id) });
      await batch.commit();
      setNewStudentName('');
    } catch (addError) {
      console.error('Failed to add student to squad', addError);
      setRosterError('Could not add the student. Please try again.');
    } finally {
      setStudentActionId(null);
    }
  };

  const saveStudentName = async (studentId: string) => {
    const displayName = studentNameDraft.trim();
    if (!displayName || studentActionId) return;
    setStudentActionId(studentId);
    setRosterError(null);
    try {
      await updateDoc(doc(db, 'users', studentId), { displayName });
      setStudents(current => current.map(student => student.id === studentId ? { ...student, displayName } : student));
      setEditingStudentId(null);
      setStudentNameDraft('');
    } catch (renameError) {
      console.error('Failed to rename student', renameError);
      setRosterError('Could not update the student name. Please try again.');
    } finally {
      setStudentActionId(null);
    }
  };

  const saveStudentDismissal = async (
    studentId: string,
    dismissalProcedureType: DismissalProcedureType | null,
    dismissalProcedureNote?: string | null,
  ) => {
    if (studentActionId) return;
    setStudentActionId(studentId);
    setRosterError(null);
    const patch = {
      dismissalProcedureType,
      dismissalProcedureNote: dismissalProcedureType === 'special'
        ? String(dismissalProcedureNote || '').trim() || null
        : null,
    };
    try {
      await updateDoc(doc(db, 'users', studentId), patch);
      setStudents(current => current.map(student => student.id === studentId ? { ...student, ...patch } : student));
    } catch (dismissalError) {
      console.error('Failed to update student dismissal information', dismissalError);
      setRosterError('Could not update the student pickup information. Please try again.');
    } finally {
      setStudentActionId(null);
    }
  };

  const removeStudent = async (student: User) => {
    if (!squadId || studentActionId) return;
    const displayName = String(student.displayName || 'this student').trim();
    const confirmed = await promptConfirm({
      title: 'Remove student from this class?',
      message: `${displayName} will be removed from this class roster. Their student profile and work will not be deleted.`,
      confirmText: 'Remove student',
    });
    if (!confirmed) return;

    setStudentActionId(student.id);
    setRosterError(null);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'squads', squadId), { studentIds: arrayRemove(student.id) });
      batch.update(doc(db, 'users', student.id), { squadIds: arrayRemove(squadId) });
      await batch.commit();
      setStudents(current => current.filter(item => item.id !== student.id));
      if (editingStudentId === student.id) setEditingStudentId(null);
    } catch (removeError) {
      console.error('Failed to remove student from squad', removeError);
      setRosterError('Could not remove the student. Please try again.');
    } finally {
      setStudentActionId(null);
    }
  };

  const updateSchoolPocContactId = async (nextIdRaw: string) => {
    const sid = String(schoolId || '').trim();
    if (!sid) {
      setError('Select a location first to assign an on-site POC.');
      return;
    }
    const nextId = String(nextIdRaw || '').trim();
    try {
      setError(null);
      await updateDoc(doc(db, 'locations', sid), { pocContactId: nextId || null });
      setSchool(current => current ? ({ ...current, pocContactId: nextId || undefined } as School) : current);
    } catch (e) {
      console.error('Failed to update school pocContactId', e);
      const msg = String((e as any)?.message || '');
      setError(msg ? `Failed to update on-site POC. (${msg})` : 'Failed to update on-site POC.');
    }
  };

  const refreshPartnerContacts = async () => {
    if (!partnerId) {
      setContacts([]);
      return;
    }
    const snapshot = await getDocs(collection(db, 'partners', partnerId, 'contacts'));
    const next = snapshot.docs.map(item => ({ id: item.id, ...(item.data() as any) })) as PartnerContact[];
    next.sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || '')));
    setContacts(next);
  };

  const resetContactEditor = () => {
    setContactFormOpen(false);
    setEditingContactId(null);
    setContactDraft({ displayName: '', email: '', phone: '', role: 'primary' });
    setContactError(null);
  };

  const beginEditContact = (contact: PartnerContact) => {
    setEditingContactId(contact.id);
    setContactFormOpen(true);
    setContactDraft({
      displayName: String(contact.displayName || ''),
      email: String(contact.email || ''),
      phone: String(contact.phone || ''),
      role: contact.roles?.[0] || 'other',
    });
    setContactError(null);
  };

  const saveContact = async (event: React.FormEvent) => {
    event.preventDefault();
    const displayName = contactDraft.displayName.trim();
    if (!partnerId || !displayName || contactSaving) return;
    const actorId = String((userData as any)?.id || currentUser?.uid || '').trim() || null;
    setContactSaving(true);
    setContactError(null);
    try {
      if (editingContactId) {
        const existing = contacts.find(contact => contact.id === editingContactId);
        await updatePartnerContact(partnerId, editingContactId, {
          displayName,
          email: contactDraft.email.trim() || null,
          phone: contactDraft.phone.trim() || null,
          roles: [contactDraft.role],
          locationIds: existing?.locationIds || (schoolId ? [schoolId] : []),
          active: existing?.active !== false,
        }, actorId);
      } else {
        await createPartnerContact(partnerId, {
          displayName,
          email: contactDraft.email.trim() || null,
          phone: contactDraft.phone.trim() || null,
          roles: [contactDraft.role],
          locationIds: schoolId ? [schoolId] : [],
          active: true,
          createdBy: actorId,
          updatedBy: actorId,
        }, actorId);
      }
      await refreshPartnerContacts();
      resetContactEditor();
    } catch (contactSaveError) {
      console.error('Failed to save partner contact', contactSaveError);
      setContactError('Could not save the contact. Please try again.');
    } finally {
      setContactSaving(false);
    }
  };

  const Missing = ({ show }: { show: boolean }) =>
    show ? <span className="squad-details-missing" title="Missing">!</span> : null;

  const missingTeacherLogistics = useMemo(() => {
    return new Set(getMissingTeacherLogistics(draft).map(x => x.key));
  }, [draft]);

  const MissingSm = ({ show }: { show: boolean }) =>
    show ? <span className="squad-details-missing squad-details-missing-sm" title="Missing">!</span> : null;

  const renderReadOnlyRow = (label: string, value: React.ReactNode, locked?: boolean, missing?: boolean) => {
    return (
      <div className="squad-details-row">
        <div className="squad-details-row-label">
          {label}
          {locked && (
            <span className="squad-details-lock" title="Managed by Overture admin">🔒</span>
          )}
        </div>
        <div className="squad-details-row-value">
          <Missing show={!!missing} />
          {value ?? '—'}
        </div>
      </div>
    );
  };

  const renderEditableRow = (
    key: string,
    label: string,
    displayValue: React.ReactNode,
    editor: React.ReactNode
  ) => {
    const isEditing = effectiveMode === 'admin' && editingKey === key;
    const isMissing = displayValue === '—' || displayValue === null || displayValue === undefined || displayValue === '';
    return (
      <div
        className={`squad-details-row ${effectiveMode === 'admin' ? 'editable' : ''} ${isEditing ? 'editing' : ''}`}
        onClick={() => {
          if (effectiveMode !== 'admin') return;
          setEditingKey(key);
        }}
        role={effectiveMode === 'admin' ? 'button' : undefined}
        tabIndex={effectiveMode === 'admin' ? 0 : -1}
        onKeyDown={(e) => {
          if (effectiveMode !== 'admin') return;
          if (e.key === 'Enter') {
            setEditingKey(key);
          }
          if (e.key === 'Escape') {
            setEditingKey(null);
          }
        }}
      >
        <div className="squad-details-row-label">{label}</div>
        <div className="squad-details-row-value">
          {!isEditing && <Missing show={!!isMissing} />}
          {isEditing ? editor : displayValue}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="squad-details-loading">Loading squad details…</div>;
  }

  if (!squadId) {
    return <div className="squad-details-loading">Missing squad id.</div>;
  }

  if (effectiveMode === 'teacher' && squad && !canViewAsTeacher) {
    return <div className="squad-details-loading">You don’t have permission to view this squad.</div>;
  }

  const showSquadCode = isMusicCurriculum({
    curriculumId: String((draft as any)?.curriculumId ?? squad?.curriculumId ?? '').trim() || undefined,
  });

  return (
    <div className="squad-details-page">
      <div className="squad-details-header">
        <button
          className="squad-details-back"
          onClick={() => { void handleBackClick(); }}
        >
          ← Back
        </button>

        <div className="squad-details-header-main">
          <div className="squad-details-title">
            {effectiveMode === 'admin' ? (
              <>
                {editingKey === 'title' ? (
                  <input
                    className="squad-details-title-input"
                    autoFocus
                    value={String((draft as any).title ?? squad?.title ?? '')}
                    onChange={(e) => updateDraft({ title: e.target.value } as any)}
                    onBlur={() => setEditingKey(null)}
                    placeholder="Squad title"
                  />
                ) : (
                  <span
                    className="squad-details-title-text editable"
                    onClick={() => setEditingKey('title')}
                    title="Click to edit title"
                  >
                    {String((draft as any).title ?? squad?.title ?? 'Squad')}
                  </span>
                )}

                {showSquadCode && (editingKey === 'code' ? (
                  <input
                    className="squad-details-code-input"
                    autoFocus
                    value={String((draft as any).code ?? squad?.code ?? '')}
                    onChange={(e) => updateDraft({ code: e.target.value.toUpperCase() } as any)}
                    onBlur={() => setEditingKey(null)}
                    placeholder="CODE"
                    maxLength={12}
                  />
                ) : (
                  <span
                    className="squad-details-code editable"
                    onClick={() => setEditingKey('code')}
                    title="Click to edit squad code"
                  >
                    Code {String((draft as any).code ?? squad?.code ?? '')}
                  </span>
                ))}
              </>
            ) : (
              <>
                {String(squad?.title || 'Squad')}
                {showSquadCode && squad?.code ? <span className="squad-details-code">Code {squad.code}</span> : null}
              </>
            )}
          </div>
          <div className="squad-details-subtitle">
            {savedAt ? `Saved just now` : dirty ? 'Unsaved changes' : 'Up to date'}
            {saving ? ' • Saving…' : ''}
          </div>
        </div>

        <div className="squad-details-header-actions">
          <button className="squad-details-primary" onClick={() => void save()} disabled={saving || !dirty}>
            Save changes
          </button>
        </div>
      </div>

      <div className="squad-details-content">
        {error && (
          <div className="squad-details-alert" role="alert">
            {error}
          </div>
        )}

        {effectiveMode === 'admin' && scheduledBillingDates.length > 0 && <div className="squad-details-card squad-details-billing-card">
          <div className="squad-details-card-title">Class-session billing</div>
          <div className="squad-details-card-body squad-details-session-list">{scheduledBillingDates.map((date) => {
            const claim = billingClaims.find((item) => item.classDate === date); const state = claim?.state || 'unbilled';
            return <div className="squad-details-session" key={date}><span>{formatMaybeDate(date)}</span><b className={`is-${state}`}>{state}</b>{claim?.invoiceId ? <button type="button" onClick={() => navigate(`/app/admin/invoices/${claim.invoiceId}`)}>Open invoice</button> : null}</div>;
          })}</div>
        </div>}

        {effectiveMode === 'admin' &&
          (payments.length > 0 || String(draft.paymentMode || (squad as any)?.paymentMode || '') === 'stripe') && (
          <div className="squad-details-card squad-details-payments-card">
            <div className="squad-details-card-title squad-details-roster-title">
              <span>💳 Stripe payments &amp; refunds</span>
              {payments.length > 0 && (
                <span className="squad-details-badge subtle">
                  {fmtUsdFromCents(payments.reduce((sum, p) => sum + Math.round(Number(p.amountCents || 0)), 0))} collected
                  {payments.some((p) => Number(p.refundedAmountCents || 0) > 0)
                    ? ` · ${fmtUsdFromCents(payments.reduce((sum, p) => sum + Math.round(Number(p.refundedAmountCents || 0)), 0))} refunded`
                    : ''}
                </span>
              )}
            </div>
            <div className="squad-details-card-body">
              {squadIsCanceled && refundablePayments.length > 0 && (
                <div className="squad-details-refund-banner" role="alert">
                  <div>
                    <strong>This class is canceled.</strong>{' '}
                    {refundablePayments.length === 1
                      ? `1 paid family has not been refunded (${fmtUsdFromCents(refundableTotalCents)}).`
                      : `${refundablePayments.length} paid families have not been refunded (${fmtUsdFromCents(refundableTotalCents)} total).`}
                  </div>
                  <button type="button" onClick={openBulkRefund}>Refund everyone…</button>
                </div>
              )}
              {paymentsLoading ? (
                <div className="squad-details-empty">Loading payments…</div>
              ) : payments.length === 0 ? (
                <div className="squad-details-empty">No Stripe payments recorded for this class yet. Payments appear here automatically after checkout.</div>
              ) : (
                <>
                  <div className="squad-details-payments-hint">
                    Click a payment to refund it. Refunds go back to the parent's original payment method, and the parent gets a confirmation email. Refunding never removes a student from the roster.
                  </div>
                  <div className="squad-details-payments-list">
                    {payments.map((p) => {
                      const remaining = paymentRemainingCents(p);
                      const refundedCents = Math.round(Number(p.refundedAmountCents || 0));
                      const chip = remaining <= 0 && refundedCents > 0 ? 'refunded' : refundedCents > 0 ? 'partial' : 'paid';
                      const clickable = isRefundablePayment(p);
                      const studentName = paymentStudentName(p);
                      const paidOn = p.createdAt?.toDate ? p.createdAt.toDate().toLocaleDateString() : null;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          className="squad-details-payment-row"
                          onClick={() => clickable && openRefundModal(p)}
                          disabled={!clickable}
                          title={clickable ? `Refund ${studentName}'s payment` : undefined}
                        >
                          <div className="squad-details-roster-avatar" aria-hidden="true">
                            {studentName.trim().slice(0, 1).toUpperCase() || '?'}
                          </div>
                          <div className="squad-details-payment-main">
                            <span className="squad-details-payment-student">{studentName}</span>
                            <small>
                              {paymentParentLabel(p)} · paid {fmtUsdFromCents(p.amountCents)}
                              {paidOn ? ` on ${paidOn}` : ''}
                            </small>
                          </div>
                          <span className={`squad-details-payment-chip is-${chip}`}>
                            {chip === 'refunded'
                              ? 'Refunded'
                              : chip === 'partial'
                                ? `${fmtUsdFromCents(refundedCents)} refunded`
                                : 'Paid'}
                          </span>
                          {clickable && <span className="squad-details-payment-cta">Refund…</span>}
                        </button>
                      );
                    })}
                  </div>
                  {refundablePayments.length > 1 && !squadIsCanceled && (
                    <button type="button" className="squad-details-refund-all" onClick={openBulkRefund}>
                      Refund everyone ({refundablePayments.length} payments · {fmtUsdFromCents(refundableTotalCents)})…
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        <div className="squad-details-grid">
          {/* Class at a glance */}
          <div className="squad-details-card">
            <div className="squad-details-card-title">Class at a glance</div>
            <div className="squad-details-card-body">
              {effectiveMode === 'admin'
                ? renderEditableRow(
                    'locationId',
                    'Location',
                    (school?.name || (draft as any).locationName || '—') as any,
                    <select
                      className="squad-details-inline-input"
                      value={String((draft as any).locationId || squad?.locationId || '')}
                      autoFocus
                      onChange={(e) => {
                        const nextId = e.target.value;
                        const match = allSchools.find(s => s.id === nextId);
                        if (!match) {
                          setEditingKey(null);
                          return;
                        }
                        updateDraft({
                          locationId: nextId,
                          locationName: match?.name,
                          // If address has not been overridden, pull from location.
                          address: String((draft as any).address || '').trim()
                            ? (draft as any).address
                            : (match?.address || undefined),
                        } as any);
                        setEditingKey(null);
                      }}
                      onBlur={() => setEditingKey(null)}
                    >
                      <option value="">{partnerId ? 'Select partner location…' : 'Partner required'}</option>
                      {allSchools.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  )
                : renderReadOnlyRow('Location', school?.name || draft.locationName || '—', false, isEmptyValue(school?.name || draft.locationName))}

              {effectiveMode === 'admin'
                ? renderEditableRow(
                    'classroomName',
                    'Classroom',
                    String(draft.classroomName || '—'),
                    <input
                      className="squad-details-inline-input"
                      type="text"
                      autoFocus
                      value={String(draft.classroomName || '')}
                      onChange={(e) => updateDraft({ classroomName: e.target.value } as any)}
                      onBlur={() => setEditingKey(null)}
                      placeholder="e.g., Room 201"
                    />
                  )
                : (
                  <div className="squad-details-row">
                    <div className="squad-details-row-label">
                      Classroom <MissingSm show={missingTeacherLogistics.has('classroomName')} />
                    </div>
                    <div className="squad-details-row-value">
                      <input
                        className="squad-details-inline-input"
                        type="text"
                        value={String(draft.classroomName || '')}
                        onChange={(e) => updateDraft({ classroomName: e.target.value } as any, true)}
                        placeholder="e.g., Room 201"
                      />
                    </div>
                  </div>
                )}

              {effectiveMode === 'admin'
                ? renderEditableRow(
                    'address',
                    'Address',
                    String((draft as any).address || school?.address || '—'),
                    <input
                      className="squad-details-inline-input"
                      type="text"
                      autoFocus
                      value={String((draft as any).address || '')}
                      onChange={(e) => updateDraft({ address: e.target.value } as any)}
                      onBlur={() => setEditingKey(null)}
                      placeholder="Address"
                    />
                  )
                : renderReadOnlyRow('Address', school?.address || (draft.address as any) || '—', false, isEmptyValue(school?.address || (draft.address as any)))}

              {effectiveMode === 'admin' && schoolId
                ? renderEditableRow(
                    'school.pocContactId',
                    'On-site POC',
                    (() => {
                      const pocId = String((school as any)?.pocContactId || '').trim();
                      const c = pocId ? contacts.find(x => x.id === pocId) : null;
                      if (c) return `${c.displayName}${c.roles?.length ? ` • ${c.roles.map(role => titleCase(String(role))).join(', ')}` : ''}${c.phone ? ` • ${c.phone}` : ''}`;
                      return String((school as any)?.pocOnSite || '—');
                    })(),
                    <select
                      className="squad-details-inline-input"
                      autoFocus
                      value={String((school as any)?.pocContactId || '')}
                      onChange={(e) => {
                        const v = e.target.value;
                        void updateSchoolPocContactId(v);
                        setEditingKey(null);
                      }}
                      onBlur={() => setEditingKey(null)}
                    >
                      <option value="">— Select contact —</option>
                      {contacts.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.displayName || 'Unnamed contact'}
                        </option>
                      ))}
                    </select>
                  )
                : renderReadOnlyRow('On-site POC', (() => {
                    const pocId = String((school as any)?.pocContactId || '').trim();
                    const c = pocId ? contacts.find(x => x.id === pocId) : null;
                    if (c) return `${c.displayName}${c.roles?.length ? ` • ${c.roles.map(role => titleCase(String(role))).join(', ')}` : ''}${c.phone ? ` • ${c.phone}` : ''}`;
                    return (draft.pocOnSite || school?.pocOnSite || '—') as any;
                  })(), false, isEmptyValue((school as any)?.pocContactId || (draft as any).pocOnSite || (school as any)?.pocOnSite))}
              {effectiveMode !== 'admin' && (
                <>
                  {renderReadOnlyRow('Schedule type', String((draft as any).scheduleKind || (squad as any)?.scheduleKind) === 'single_day' ? 'Single-day event' : 'Recurring program', false, false)}
                  {String((draft as any).scheduleKind || (squad as any)?.scheduleKind) !== 'single_day' && renderReadOnlyRow('Day of week', String(draft.schedule?.dayOfWeek || squad?.schedule?.dayOfWeek || '—'), false, isEmptyValue(draft.schedule?.dayOfWeek || squad?.schedule?.dayOfWeek))}
                  {String((draft as any).scheduleKind || (squad as any)?.scheduleKind) === 'single_day' && renderReadOnlyRow('Event date', formatMaybeDate((draft as any).schedule?.mainDate || (squad as any)?.schedule?.mainDate), false, isEmptyValue((draft as any).schedule?.mainDate || (squad as any)?.schedule?.mainDate))}
                  {String((draft as any).scheduleKind || (squad as any)?.scheduleKind) === 'single_day' && renderReadOnlyRow('Event kind', titleCase(String((draft as any).singleDayType || (squad as any)?.singleDayType || 'class')), false, false)}
                  {renderReadOnlyRow(
                    'Time',
                    `${formatTime12h(draft.schedule?.startTime || squad?.schedule?.startTime)} – ${formatTime12h(draft.schedule?.endTime || squad?.schedule?.endTime)}`,
                    false,
                    isEmptyValue(draft.schedule?.startTime || squad?.schedule?.startTime) || isEmptyValue(draft.schedule?.endTime || squad?.schedule?.endTime)
                  )}
                  {renderReadOnlyRow('Format', formatDeliveryMode((draft as any).deliveryMode ?? (squad as any)?.deliveryMode ?? 'in_person'), false, false)}
                  {renderReadOnlyRow('Time zone', (() => {
                    const raw = String((draft as any).timezone || squad?.timezone || '').trim();
                    const inherited = String((school as any)?.timezone || partnerTimezone || DEFAULT_TZ).trim();
                    const tz = raw || inherited;
                    const opt = timezoneOptions.find(o => String(o.id) === tz);
                    const label = opt?.label || tz;
                    return raw ? label : `${label} (inherited)`;
                  })(), false, false)}
                  {String((draft as any).scheduleKind || (squad as any)?.scheduleKind) !== 'single_day' && renderReadOnlyRow('Start date', formatMaybeDate(draft.schedule?.startDate || squad?.schedule?.startDate), false, isEmptyValue(draft.schedule?.startDate || squad?.schedule?.startDate))}
                  {String((draft as any).scheduleKind || (squad as any)?.scheduleKind) !== 'single_day' && renderReadOnlyRow('End date', formatMaybeDate(draft.schedule?.endDate || squad?.schedule?.endDate), false, isEmptyValue(draft.schedule?.endDate || squad?.schedule?.endDate))}
                  {String((draft as any).scheduleKind || (squad as any)?.scheduleKind) !== 'single_day' && renderReadOnlyRow('No class days', <span className="squad-details-date-summary">{formatDateList(draft.schedule?.exceptions || squad?.schedule?.exceptions)}</span>, false, false)}
                  {String((draft as any).scheduleKind || (squad as any)?.scheduleKind) !== 'single_day' && normalizeDateList(draft.schedule?.inheritedExceptions ?? squad?.schedule?.inheritedExceptions).length > 0 && renderReadOnlyRow('No class (from partner calendar)', <span className="squad-details-date-summary">{formatDateList(draft.schedule?.inheritedExceptions ?? squad?.schedule?.inheritedExceptions)}</span>, false, false)}
                  {String((draft as any).scheduleKind || (squad as any)?.scheduleKind) !== 'single_day' && renderReadOnlyRow('Make-up days', <span className="squad-details-date-summary">{formatDateList(draft.schedule?.makeups || squad?.schedule?.makeups)}</span>, false, false)}
                  {renderReadOnlyRow('Grades', `${draft.startGrade || '—'} – ${draft.endGrade || '—'}`, false, isEmptyValue(draft.startGrade) || isEmptyValue(draft.endGrade))}
                  {renderReadOnlyRow('Lead teacher', (() => {
                    const id = String((draft as any).regularTeacher ?? (squad as any)?.regularTeacher ?? (squad as any)?.leadTeacherId ?? '');
                    if (!id) return '—';
                    const teacher = teacherDirectory.find((candidate) => String(candidate.id) === id);
                    return teacher?.displayName || teacher?.email || userNameById[id] || 'Assigned teacher';
                  })(), false, isEmptyValue((draft as any).regularTeacher ?? (squad as any)?.regularTeacher ?? (squad as any)?.leadTeacherId))}
                  {renderReadOnlyRow('Status', String(draft.status || squad?.status || '—'), false, isEmptyValue(draft.status || squad?.status))}
                </>
              )}
              {renderReadOnlyRow('Last updated', (() => {
                const t = (squad as any)?.detailsUpdatedAt;
                const by = String((squad as any)?.detailsUpdatedBy || '').trim();
                const byLabel = by ? (userNameById[by] || by) : '';
                if (!t) return '—';
                return `${new Date(Number(t)).toLocaleString()}${byLabel ? ` • ${byLabel}` : ''}`;
              })())}
              {effectiveMode === 'admin' && renderReadOnlyRow('Last teacher update', (() => {
                const t = (squad as any)?.teacherDetailsUpdatedAt;
                const by = String((squad as any)?.teacherDetailsUpdatedBy || '').trim();
                const byLabel = by ? (userNameById[by] || by) : '';
                if (!t) return '—';
                return `${new Date(Number(t)).toLocaleString()}${byLabel ? ` • ${byLabel}` : ''}`;
              })())}

              {effectiveMode === 'admin' && (
                <>
                  <div className="squad-details-divider" />
                  {(() => {
                    const pm = String(draft.paymentMode || (squad as any)?.paymentMode || 'stripe');
                    const showFlat = pm === 'school_flat_fee';
                    const showTuition = pm === 'stripe' || pm === 'school_enrollment' || pm === 'external';
                    const scheduleKind = String((draft as any).scheduleKind || (squad as any)?.scheduleKind || 'recurring');
                    const isSingleDay = scheduleKind === 'single_day';

                    return (
                      <>
                  {renderEditableRow(
                    'regularTeacher',
                    'Lead teacher',
                    (() => {
                      const id = String((draft as any).regularTeacher ?? (squad as any)?.regularTeacher ?? '');
                      if (!id) return '—';
                      const u = teacherDirectory.find(t => String(t.id) === id);
                      return u?.displayName || u?.email || id;
                    })(),
                    <div onClick={(e) => e.stopPropagation()}>
                      <SearchableSelect
                        valueId={String((draft as any).regularTeacher ?? (squad as any)?.regularTeacher ?? '')}
                        placeholder="Search teachers…"
                        query={leadTeacherQuery}
                        onQueryChange={setLeadTeacherQuery}
                        onResetQueryToValue={() => setLeadTeacherQuery('')}
                        showIdInMenu={false}
                        autoFocus={true}
                        options={[{ id: '', label: 'Unassigned' }, ...teacherSelectOptions]}
                        onSelectId={(id) => {
                          updateDraft({ ...(draft as any), regularTeacher: String(id || '') } as any);
                          setLeadTeacherQuery('');
                          setEditingKey(null);
                        }}
                      />
                    </div>
                  )}
                  {(() => {
                    const leadId = String((draft as any).regularTeacher ?? (squad as any)?.regularTeacher ?? '');
                    const coTeachers: Array<{ teacherId: string; adminPayHourlyRate?: number | null }> =
                      Array.isArray((draft as any).coTeachers)
                        ? (draft as any).coTeachers
                        : (Array.isArray((squad as any)?.coTeachers) ? (squad as any).coTeachers : []);
                    const setCoTeachers = (next: Array<{ teacherId: string; adminPayHourlyRate?: number | null }>) =>
                      updateDraft({ ...(draft as any), coTeachers: next } as any);
                    const staffPay: Record<string, number | null> =
                      ((draft as any).staffPayByTeacherId ?? (squad as any)?.staffPayByTeacherId) || {};
                    const setStaffPay = (next: Record<string, number | null>) =>
                      updateDraft({ ...(draft as any), staffPayByTeacherId: next } as any);
                    const staffRoles: Record<string, SquadStaffRole> =
                      ((draft as any).staffRolesByTeacherId ?? (squad as any)?.staffRolesByTeacherId) || {};
                    const coIds = coTeachers.map(c => String(c.teacherId));
                    const otherStaffIds = Array.from(new Set([
                      ...((squad?.teacherIds || []) as string[]),
                      ...Object.keys(staffPay),
                    ])).filter(id => id && id !== leadId && !coIds.includes(id));
                    const teacherLabel = (id: string) => {
                      const u = teacherDirectory.find(t => String(t.id) === id);
                      return u?.displayName || u?.email || userNameById[id] || id;
                    };
                    const leadRateValue = (draft as any).adminPayHourlyRate ?? (squad as any)?.adminPayHourlyRate;
                    const payInput = (
                      value: string,
                      onChange: (raw: string) => void,
                      label: string,
                    ) => (
                      <span className="squad-details-team-pay">
                        <span>$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={label}
                          value={value}
                          placeholder="—"
                          onChange={(e) => onChange(e.target.value)}
                        />
                        <span>/hr</span>
                      </span>
                    );
                    return (
                      <div className="squad-details-team" onClick={(e) => e.stopPropagation()}>
                        <div className="squad-details-team-head">
                          <span>Teaching team &amp; pay</span>
                          <small>$/hr replaces normal payroll rate for this class. Blank = normal rate.</small>
                        </div>

                        {leadId ? (
                          <div className="squad-details-team-row">
                            <span className="squad-details-team-role is-lead">Lead</span>
                            <span className="squad-details-team-name">{teacherLabel(leadId)}</span>
                            {payInput(
                              leadRateInput ?? (leadRateValue == null ? '' : Number(leadRateValue).toFixed(2)),
                              (raw) => {
                                setLeadRateInput(raw);
                                updateDraft({ ...(draft as any), adminPayHourlyRate: parseUsdToDollars(raw) } as any);
                              },
                              `Admin pay per hour for ${teacherLabel(leadId)}`,
                            )}
                          </div>
                        ) : (
                          <div className="squad-details-team-empty">No lead teacher assigned yet.</div>
                        )}

                        <div className="squad-details-team-group-head">
                          <strong>Co-lead teachers</strong>
                          <small>Teach alongside the lead teacher.</small>
                        </div>
                        <SearchableSelect
                          valueId=""
                          placeholder="+ Add co-lead teacher…"
                          query={coTeacherQuery}
                          onQueryChange={setCoTeacherQuery}
                          onResetQueryToValue={() => setCoTeacherQuery('')}
                          showIdInMenu={false}
                          options={teacherSelectOptions.filter(o =>
                            o.id !== leadId && !coTeachers.some(c => String(c.teacherId) === o.id))}
                          onSelectId={(id) => {
                            const cleaned = String(id || '').trim();
                            if (cleaned && !coTeachers.some(c => String(c.teacherId) === cleaned)) {
                              setCoTeachers([...coTeachers, { teacherId: cleaned, adminPayHourlyRate: null }]);
                            }
                            setCoTeacherQuery('');
                          }}
                        />

                        {coTeachers.map((c, idx) => {
                          const id = String(c?.teacherId || '');
                          return (
                            <div key={`${id}-${idx}`} className="squad-details-team-row">
                              <span className="squad-details-team-role is-colead">Co-lead</span>
                              <span className="squad-details-team-name">{teacherLabel(id)}</span>
                              {payInput(
                                coTeacherRateInputs[id] ?? (c.adminPayHourlyRate == null ? '' : Number(c.adminPayHourlyRate).toFixed(2)),
                                (raw) => {
                                  setCoTeacherRateInputs(prev => ({ ...prev, [id]: raw }));
                                  setCoTeachers(coTeachers.map((x, xi) => xi === idx
                                    ? { ...x, adminPayHourlyRate: parseUsdToDollars(raw) }
                                    : x));
                                },
                                `Admin pay per hour for ${teacherLabel(id)}`,
                              )}
                              <button
                                type="button"
                                className="squad-details-team-remove"
                                title="Remove co-lead teacher"
                                onClick={() => setCoTeachers(coTeachers.filter((_, xi) => xi !== idx))}
                              >
                                ✕
                              </button>
                            </div>
                          );
                        })}

                        <div className="squad-details-team-group-head squad-details-team-group-head--staff">
                          <strong>Other staff</strong>
                          <small>Viewers and non-teaching support roles.</small>
                        </div>
                        <div className="squad-details-team-staff-adder">
                          <select aria-label="Role for new staff member" value={staffRoleToAdd} onChange={(event) => setStaffRoleToAdd(event.target.value as SquadStaffRole)}>
                            {SQUAD_STAFF_ROLE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                          </select>
                          <SearchableSelect
                            valueId=""
                            placeholder="+ Add staff…"
                            query={staffMemberQuery}
                            onQueryChange={setStaffMemberQuery}
                            onResetQueryToValue={() => setStaffMemberQuery('')}
                            showIdInMenu={false}
                            options={teacherSelectOptions.filter(o =>
                              o.id !== leadId && !coIds.includes(o.id) && !otherStaffIds.includes(o.id))}
                            onSelectId={(id) => {
                              const cleaned = String(id || '').trim();
                              if (cleaned && !Object.prototype.hasOwnProperty.call(staffPay, cleaned)) {
                                updateDraft({
                                  ...(draft as any),
                                  staffPayByTeacherId: { ...staffPay, [cleaned]: null },
                                  staffRolesByTeacherId: { ...staffRoles, [cleaned]: staffRoleToAdd },
                                } as any);
                              }
                              setStaffMemberQuery('');
                            }}
                          />
                        </div>

                        {otherStaffIds.map((id) => (
                          <div key={id} className="squad-details-team-row">
                            <select className="squad-details-team-role is-staff" aria-label={`Role for ${teacherLabel(id)}`} value={staffRoles[id] || 'other'} onChange={(event) => {
                              const nextRole = event.target.value as SquadStaffRole;
                              updateDraft({
                                ...(draft as any),
                                staffRolesByTeacherId: { ...staffRoles, [id]: nextRole },
                                staffPayByTeacherId: nextRole === 'viewer' ? { ...staffPay, [id]: null } : staffPay,
                              } as any);
                              if (nextRole === 'viewer') setStaffRateInputs(prev => ({ ...prev, [id]: '' }));
                            }}>
                              {SQUAD_STAFF_ROLE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                            </select>
                            <span className="squad-details-team-name">{teacherLabel(id)}</span>
                            {(staffRoles[id] || 'other') !== 'viewer' && payInput(
                              staffRateInputs[id] ?? (staffPay[id] == null ? '' : Number(staffPay[id]).toFixed(2)),
                              (raw) => {
                                setStaffRateInputs(prev => ({ ...prev, [id]: raw }));
                                setStaffPay({ ...staffPay, [id]: parseUsdToDollars(raw) });
                              },
                              `Admin pay per hour for ${teacherLabel(id)}`,
                            )}
                            <button
                              type="button"
                              className="squad-details-team-remove"
                              title={`Remove ${teacherLabel(id)}'s squad access`}
                              onClick={() => {
                                void (async () => {
                                  const ok = await promptConfirm({
                                    title: 'Remove squad access?',
                                    message: `${teacherLabel(id)} will lose access to this class's dashboard and roster.`,
                                    confirmText: 'Remove access',
                                  });
                                  if (!ok || !squadId) return;
                                  const nextPay = { ...staffPay };
                                  delete nextPay[id];
                                  const nextRoles = { ...staffRoles };
                                  delete nextRoles[id];
                                  updateDraft({
                                    ...(draft as any),
                                    staffPayByTeacherId: nextPay,
                                    staffRolesByTeacherId: nextRoles,
                                  } as any);
                                  if ((squad?.teacherIds || []).includes(id)) {
                                    await updateDoc(doc(db, 'squads', squadId), { teacherIds: arrayRemove(id) } as any);
                                  }
                                  await removeTeacherFromSquadEvents({ squadId, teacherId: id });
                                })();
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}

                        {upcomingCoverage.length > 0 && (
                          <div className="squad-details-team-subs">
                            {upcomingCoverage.map((cov) => (
                              <div key={cov.id} className="squad-details-team-sub-row">
                                <span>{cov.start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                                <span>{cov.role === 'co_teacher' ? 'Co-teacher' : 'Lead'} · {teacherLabel(cov.requestedTeacherId)}</span>
                                <b className={cov.status === 'claimed' ? 'is-covered' : 'is-open'}>
                                  {cov.status === 'claimed'
                                    ? `Covered by ${teacherLabel(cov.substituteTeacherId || '')}`
                                    : 'Sub needed'}
                                </b>
                              </div>
                            ))}
                          </div>
                        )}
                        <small className="squad-details-team-hint">
                          Subs are per class date — request one from the calendar or teacher dashboard.
                        </small>
                      </div>
                    );
                  })()}

                  {renderEditableRow(
                    'scheduleKind',
                    'Schedule type',
                    isSingleDay ? 'Single-day event' : 'Recurring program',
                    <select className="squad-details-inline-input" value={scheduleKind} autoFocus onChange={(e) => {
                      const next = e.target.value as 'recurring' | 'single_day';
                      const schedule = { ...(draft.schedule || {}) };
                      if (next === 'recurring') delete schedule.mainDate;
                      updateDraft({
                        ...(draft as any),
                        scheduleKind: next,
                        schedule,
                        ...(next === 'single_day' ? { singleDayType: (draft as any).singleDayType || 'class' } : {}),
                      } as any);
                      setEditingKey(null);
                    }} onBlur={() => setEditingKey(null)}><option value="recurring">Recurring program</option><option value="single_day">Single-day event</option></select>
                  )}

                  {!isSingleDay && renderEditableRow(
                    'schedule.dayOfWeek',
                    'Day of week',
                    (() => {
                      const id = String(draft.schedule?.dayOfWeek || '');
                      const found = DAY_OPTIONS.find(x => x.id === id);
                      return found?.label || (id || '—');
                    })(),
                    <select
                      className="squad-details-inline-input"
                      value={String(draft.schedule?.dayOfWeek || '')}
                      autoFocus
                      onChange={(e) => {
                        updateSchedule({ dayOfWeek: e.target.value });
                        setEditingKey(null);
                      }}
                      onBlur={() => setEditingKey(null)}
                    >
                      <option value="">Select…</option>
                      {DAY_OPTIONS.map(o => (<option key={o.id} value={o.id}>{o.label}</option>))}
                    </select>
                  )}
                  {isSingleDay && renderEditableRow(
                    'singleDayType',
                    'Event kind',
                    titleCase(String((draft as any).singleDayType || (squad as any)?.singleDayType || 'class')),
                    <select className="squad-details-inline-input" value={String((draft as any).singleDayType || (squad as any)?.singleDayType || 'class')} autoFocus onChange={(e) => { updateDraft({ ...(draft as any), singleDayType: e.target.value } as any); setEditingKey(null); }} onBlur={() => setEditingKey(null)}><option value="class">Class</option><option value="demo">Demo</option></select>
                  )}
                  {isSingleDay && String((draft as any).singleDayType || (squad as any)?.singleDayType || 'class') === 'demo' && renderEditableRow(
                    'demoType',
                    'Demo type',
                    titleCase(String((draft as any).demoType || (squad as any)?.demoType || '')) || '—',
                    <select className="squad-details-inline-input" value={String((draft as any).demoType || (squad as any)?.demoType || '')} autoFocus onChange={(e) => { updateDraft({ ...(draft as any), demoType: e.target.value } as any); setEditingKey(null); }} onBlur={() => setEditingKey(null)}><option value="">Select…</option><option value="lunchtime">Lunch</option><option value="event_booth">Booth</option><option value="school_assembly">Assembly</option><option value="classroom_visit">Classroom</option><option value="conference">Conference</option><option value="other">Other</option></select>
                  )}
                  {isSingleDay && renderEditableRow(
                    'schedule.mainDate',
                    'Event date',
                    formatMaybeDate((draft as any).schedule?.mainDate || (squad as any)?.schedule?.mainDate),
                    <input className="squad-details-inline-input" type="date" autoFocus value={String((draft as any).schedule?.mainDate || (squad as any)?.schedule?.mainDate || '')} onChange={(e) => updateSchedule({ mainDate: e.target.value } as any)} onBlur={() => setEditingKey(null)} />
                  )}
                  {renderEditableRow(
                    'schedule.startTime',
                    'Start time',
                    formatTime12h(draft.schedule?.startTime || squad?.schedule?.startTime),
                    <input
                      className="squad-details-inline-input"
                      type="time"
                      autoFocus
                      value={String(draft.schedule?.startTime || '')}
                      onChange={(e) => updateSchedule({ startTime: e.target.value })}
                      onBlur={() => setEditingKey(null)}
                    />
                  )}
                  {renderEditableRow(
                    'schedule.endTime',
                    'End time',
                    formatTime12h(draft.schedule?.endTime || squad?.schedule?.endTime),
                    <input
                      className="squad-details-inline-input"
                      type="time"
                      autoFocus
                      value={String(draft.schedule?.endTime || '')}
                      onChange={(e) => updateSchedule({ endTime: e.target.value })}
                      onBlur={() => setEditingKey(null)}
                    />
                  )}
                  {renderEditableRow(
                    'deliveryMode',
                    'Format',
                    formatDeliveryMode((draft as any).deliveryMode ?? (squad as any)?.deliveryMode ?? 'in_person'),
                    <select
                      className="squad-details-inline-input"
                      value={String((draft as any).deliveryMode ?? (squad as any)?.deliveryMode ?? 'in_person')}
                      autoFocus
                      onChange={(e) => {
                        const v = String(e.target.value || '').trim();
                        updateDraft({ ...(draft as any), deliveryMode: v ? v : undefined } as any);
                        setEditingKey(null);
                      }}
                      onBlur={() => setEditingKey(null)}
                    >
                      <option value="">Select…</option>
                      <option value="in_person">In-person</option>
                      <option value="online">Online</option>
                    </select>
                  )}
                  {renderEditableRow(
                    'timezone',
                    'Time zone',
                    (() => {
                      const raw = String((draft as any).timezone || squad?.timezone || '').trim();
                      const effective = raw || String((school as any)?.timezone || partnerTimezone || DEFAULT_TZ).trim();
                      const opt = timezoneOptions.find(o => String(o.id) === effective);
                      return `${opt?.label || effective}${raw ? '' : ' · Inherited'}`;
                    })(),
                    <select
                      className="squad-details-inline-input"
                      value={String((draft as any).timezone || squad?.timezone || (school as any)?.timezone || partnerTimezone || DEFAULT_TZ)}
                      autoFocus
                      onChange={(e) => {
                        updateDraft({ ...(draft as any), timezone: e.target.value } as any);
                        setEditingKey(null);
                      }}
                      onBlur={() => setEditingKey(null)}
                    >
                      {ensureCurrentOption(timezoneOptions, String((draft as any).timezone || squad?.timezone || (school as any)?.timezone || partnerTimezone || DEFAULT_TZ)).map(o => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                      ))}
                    </select>
                  )}
                  {!isSingleDay && renderEditableRow(
                    'schedule.startDate',
                    'Start date',
                    formatMaybeDate(draft.schedule?.startDate || squad?.schedule?.startDate),
                    <input
                      className="squad-details-inline-input"
                      type="date"
                      autoFocus
                      value={String(draft.schedule?.startDate || '')}
                      onChange={(e) => updateSchedule({ startDate: e.target.value })}
                      onBlur={() => setEditingKey(null)}
                    />
                  )}
                  {!isSingleDay && renderEditableRow(
                    'schedule.endDate',
                    'End date',
                    formatMaybeDate(draft.schedule?.endDate || squad?.schedule?.endDate),
                    <input
                      className="squad-details-inline-input"
                      type="date"
                      autoFocus
                      value={String(draft.schedule?.endDate || '')}
                      onChange={(e) => updateSchedule({ endDate: e.target.value })}
                      onBlur={() => setEditingKey(null)}
                    />
                  )}
                  {!isSingleDay && renderEditableRow(
                    'schedule.exceptions',
                    'No class days',
                    <span className="squad-details-date-summary">{formatDateList(draft.schedule?.exceptions ?? squad?.schedule?.exceptions)}</span>,
                    <DateListEditor
                      kind="exceptions"
                      value={draft.schedule?.exceptions ?? squad?.schedule?.exceptions}
                      placeholder="No exceptions"
                      onChange={(next) => updateSchedule({ exceptions: next })}
                    />
                  )}
                  {!isSingleDay && normalizeDateList(draft.schedule?.inheritedExceptions ?? squad?.schedule?.inheritedExceptions).length > 0 && renderReadOnlyRow(
                    'No class (from partner calendar)',
                    <span className="squad-details-date-summary">{formatDateList(draft.schedule?.inheritedExceptions ?? squad?.schedule?.inheritedExceptions)}</span>,
                    false,
                    false
                  )}
                  {!isSingleDay && renderEditableRow(
                    'schedule.makeups',
                    'Make-up days',
                    <span className="squad-details-date-summary">{formatDateList(draft.schedule?.makeups ?? squad?.schedule?.makeups)}</span>,
                    <DateListEditor
                      kind="makeups"
                      value={draft.schedule?.makeups ?? squad?.schedule?.makeups}
                      placeholder="No make-ups"
                      onChange={(next) => updateSchedule({ makeups: next })}
                    />
                  )}
                  {renderEditableRow(
                    'status',
                    'Status',
                    titleCase(String(draft.status || squad?.status || '—')),
                    <select
                      className="squad-details-inline-input"
                      value={String(draft.status || '')}
                      autoFocus
                      onChange={(e) => {
                        updateDraft({ status: e.target.value as any } as any);
                        setEditingKey(null);
                      }}
                      onBlur={() => setEditingKey(null)}
                    >
                      <option value="">Select…</option>
                      <option value="lead">Lead</option>
                      <option value="published">Published</option>
                      <option value="draft">Draft</option>
                      <option value="archived">Archived</option>
                      <option value="canceled">Canceled</option>
                    </select>
                  )}
                  {renderEditableRow(
                    'capacity',
                    'Capacity',
                    String(draft.capacity ?? squad?.capacity ?? 12),
                    <input
                      className="squad-details-inline-input"
                      type="number"
                      autoFocus
                      value={String(draft.capacity ?? squad?.capacity ?? 12)}
                      onChange={(e) => updateDraft({ capacity: Number(e.target.value || 0) } as any)}
                      onBlur={() => setEditingKey(null)}
                    />
                  )}
                  {renderEditableRow(
                    'minEnroll',
                    'Min enrollment',
                    String(draft.minEnroll ?? squad?.minEnroll ?? (partnerDefaultProgramPriceCents && partnerDefaultProgramPriceCents > 0 ? Math.floor(10000 / partnerDefaultProgramPriceCents) + 1 : '—')),
                    <input
                      className="squad-details-inline-input"
                      type="number"
                      autoFocus
                      value={String(draft.minEnroll ?? squad?.minEnroll ?? (partnerDefaultProgramPriceCents && partnerDefaultProgramPriceCents > 0 ? Math.floor(10000 / partnerDefaultProgramPriceCents) + 1 : ''))}
                      onChange={(e) => updateDraft({ minEnroll: Number(e.target.value || 0) } as any)}
                      onBlur={() => setEditingKey(null)}
                    />
                  )}
                  {renderEditableRow(
                    'startGrade',
                    'Start grade',
                    (() => {
                      const id = String(draft.startGrade || '');
                      const found = gradeStartOptions.find(x => x.id === id);
                      return found?.label || (id || '—');
                    })(),
                    <select
                      className="squad-details-inline-input"
                      value={String(draft.startGrade || '')}
                      autoFocus
                      onChange={(e) => {
                        updateDraft({ startGrade: e.target.value } as any);
                        setEditingKey(null);
                      }}
                      onBlur={() => setEditingKey(null)}
                    >
                      <option value="">Select…</option>
                      {ensureCurrentOption(gradeStartOptions, String(draft.startGrade || '')).map(o => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                      ))}
                    </select>
                  )}
                  {renderEditableRow(
                    'endGrade',
                    'End grade',
                    (() => {
                      const id = String(draft.endGrade || '');
                      const found = gradeEndOptions.find(x => x.id === id);
                      return found?.label || (id || '—');
                    })(),
                    <select
                      className="squad-details-inline-input"
                      value={String(draft.endGrade || '')}
                      autoFocus
                      onChange={(e) => {
                        updateDraft({ endGrade: e.target.value } as any);
                        setEditingKey(null);
                      }}
                      onBlur={() => setEditingKey(null)}
                    >
                      <option value="">Select…</option>
                      {ensureCurrentOption(gradeEndOptions, String(draft.endGrade || '')).map(o => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                      ))}
                    </select>
                  )}
                  {!(isSingleDay && String((draft as any).singleDayType || (squad as any)?.singleDayType || 'class') === 'demo') && renderEditableRow(
                    'curriculumId',
                    'Curriculum',
                    (() => {
                      const id = String((draft as any).curriculumId || '');
                      const found = curriculumOptions.find(x => x.id === id);
                      return found?.label || (id || '—');
                    })(),
                    <select
                      className="squad-details-inline-input"
                      value={String((draft as any).curriculumId || '')}
                      autoFocus
                      onChange={(e) => {
                        updateDraft({ ...(draft as any), curriculumId: e.target.value } as any);
                        setEditingKey(null);
                      }}
                      onBlur={() => setEditingKey(null)}
                    >
                      <option value="">Select…</option>
                      {ensureCurrentOption(curriculumOptions, String((draft as any).curriculumId || '')).map(o => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                      ))}
                    </select>
                  )}
                  {!isSingleDay && squad && <CurriculumScheduleEditor
                    squad={{ ...squad, ...draft, completedLevels: squad.completedLevels, classStatus: squad.classStatus, currentLessonId: squad.currentLessonId } as Squad}
                    options={curriculumOptions}
                    onChange={sessionPlan => { if (!sessionPlanEditedRef.current) sessionPlanBaselineRef.current = squad; sessionPlanEditedRef.current = true; updateDraft({ sessionPlan }); }}
                  />}
                  {isSingleDay && String((draft as any).singleDayType || (squad as any)?.singleDayType || 'class') === 'demo' && renderEditableRow(
                    'demoExperienceId',
                    'Demo experience',
                    demoExperiences.find((item) => item.id === String((draft as any).demoExperienceId || (squad as any)?.demoExperienceId || ''))?.title || '—',
                    <select className="squad-details-inline-input" value={String((draft as any).demoExperienceId || (squad as any)?.demoExperienceId || '')} autoFocus onChange={(e) => { updateDraft({ ...(draft as any), demoExperienceId: e.target.value } as any); setEditingKey(null); }} onBlur={() => setEditingKey(null)}><option value="">Select…</option>{demoExperiences.map((item) => { const min = item.durationMinHours ?? item.durationHours ?? 1; const max = item.durationMaxHours ?? item.durationHours ?? min; return <option key={item.id} value={item.id}>{item.title} · {min === max ? min : `${min}–${max}`} hr</option>; })}</select>
                  )}
                  {isSingleDay && String((draft as any).singleDayType || (squad as any)?.singleDayType || 'class') === 'demo' && (() => {
                    const flyer = (draft as any).demoFlyer !== undefined ? (draft as any).demoFlyer : (squad as any)?.demoFlyer;
                    return <div
                      className={`squad-details-demo-flyer${draggingDemoFlyer ? ' is-dragging' : ''}`}
                      onDragEnter={(event) => { event.preventDefault(); if (!uploadingDemoFlyer) setDraggingDemoFlyer(true); }}
                      onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = uploadingDemoFlyer ? 'none' : 'copy'; }}
                      onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDraggingDemoFlyer(false); }}
                      onDrop={(event) => { event.preventDefault(); setDraggingDemoFlyer(false); const file = event.dataTransfer.files?.[0]; if (file) void uploadDemoFlyer(file); }}
                    >
                      <div className="squad-details-demo-flyer-heading"><div><strong>School-specific demo flyer</strong><small>Overrides the general flyer for this demo experience.</small></div>{flyer?.url ? <a href={flyer.url} target="_blank" rel="noreferrer">View current ↗</a> : null}</div>
                      <input className="squad-details-inline-input" type="url" aria-label="School-specific flyer URL" placeholder="Paste a flyer URL…" value={String(flyer?.url || '')} onChange={(event) => updateDraft({ demoFlyer: { title: flyer?.title || 'School-specific demo flyer', url: event.target.value } } as Partial<Squad>)} />
                      <div className="squad-details-demo-flyer-upload"><span>or</span><label className="squad-details-inline-trigger">{uploadingDemoFlyer ? 'Uploading…' : flyer?.url ? 'Replace with a file' : 'Upload PDF or image'}<input type="file" accept="application/pdf,image/*" disabled={uploadingDemoFlyer} onChange={(event) => { const file = event.target.files?.[0]; if (file) void uploadDemoFlyer(file); event.target.value = ''; }} /></label><small>Drag and drop also works here.</small></div>
                      {flyer?.url ? <button type="button" className="squad-details-demo-flyer-remove" onClick={() => updateDraft({ demoFlyer: null } as Partial<Squad>)}>Remove override and use general flyer</button> : null}
                    </div>;
                  })()}
                  {renderEditableRow(
                    'paymentMode',
                    'Payment mode',
                    titleCase(String(draft.paymentMode || 'stripe')),
                    <select
                      className="squad-details-inline-input"
                      value={String(draft.paymentMode || 'stripe')}
                      autoFocus
                      onChange={(e) => void changeSquadPaymentMode(e.target.value)}
                      onBlur={() => setEditingKey(null)}
                    >
                      <option value="stripe">Stripe</option>
                      <option value="school_enrollment">School (Enrollment)</option>
                      <option value="school_flat_fee">School (Flat fee)</option>
                      <option value="external">External (legacy)</option>
                    </select>
                  )}
                  {partnerDefaultPaymentMode && (
                    <div className="muted" style={{ marginTop: -6, marginBottom: 8, fontSize: 12 }}>
                      Partner default: {titleCase(partnerDefaultPaymentMode)}
                    </div>
                  )}
                  {showTuition && renderEditableRow(
                    'tuitionPerStudentPerClassCents',
                    'Tuition / student / hour',
                    (() => {
                      const explicit = (draft as any).tuitionPerStudentPerClassCents ?? (squad as any)?.tuitionPerStudentPerClassCents;
                      return explicit == null && partnerDefaultProgramPriceCents != null
                        ? `${formatCentsUsd(partnerDefaultProgramPriceCents)} · Partner default`
                        : formatCentsUsd(explicit);
                    })(),
                    <input
                      className="squad-details-inline-input"
                      type="text"
                      autoFocus
                      inputMode="decimal"
                      value={priceInputByField.tuitionPerStudentPerClassCents ?? ''}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setPriceInputByField(prev => ({ ...prev, tuitionPerStudentPerClassCents: raw }));
                        const cents = parseUsdToCents(raw);
                        updateDraft({ ...(draft as any), tuitionPerStudentPerClassCents: cents, priceSource: 'squad_override' } as any);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={() => setEditingKey(null)}
                      placeholder="e.g. 12.50"
                    />
                  )}
                  {showFlat && renderEditableRow(
                    'flatFeePerClassCents',
                    'Flat fee / class',
                    (() => {
                      const explicit = (draft as any).flatFeePerClassCents ?? (squad as any)?.flatFeePerClassCents;
                      return explicit == null && partnerDefaultProgramPriceCents != null
                        ? `${formatCentsUsd(partnerDefaultProgramPriceCents)} · Partner default`
                        : formatCentsUsd(explicit);
                    })(),
                    <input
                      className="squad-details-inline-input"
                      type="text"
                      autoFocus
                      inputMode="decimal"
                      value={priceInputByField.flatFeePerClassCents ?? ''}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setPriceInputByField(prev => ({ ...prev, flatFeePerClassCents: raw }));
                        const cents = parseUsdToCents(raw);
                        updateDraft({ ...(draft as any), flatFeePerClassCents: cents, priceSource: 'squad_override' } as any);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={() => setEditingKey(null)}
                      placeholder="e.g. 240.00"
                    />
                  )}
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>

          <div className="squad-details-card squad-details-roster-card">
            <div className="squad-details-card-title squad-details-roster-title">
              <span>Students in this class</span>
              <span className="squad-details-badge subtle">{students.length} {students.length === 1 ? 'student' : 'students'}</span>
            </div>
            <div className="squad-details-card-body">
              <form className="squad-details-roster-add" onSubmit={addStudent}>
                <input
                  type="text"
                  value={newStudentName}
                  onChange={(event) => {
                    setNewStudentName(event.target.value);
                    if (rosterError) setRosterError(null);
                  }}
                  placeholder="Student name"
                  aria-label="New student name"
                  maxLength={100}
                  disabled={studentActionId === 'new'}
                />
                <button type="submit" disabled={!newStudentName.trim() || studentActionId === 'new'}>
                  {studentActionId === 'new' ? 'Adding…' : '+ Add student'}
                </button>
              </form>

              {rosterError && <div className="squad-details-roster-error" role="alert">{rosterError}</div>}

              {studentsLoading ? (
                <div className="squad-details-empty">Loading students…</div>
              ) : students.length === 0 ? (
                <div className="squad-details-empty">No students have been added to this class yet.</div>
              ) : (
                <div className="squad-details-roster-list">
                  {students.map((student) => {
                    const isEditing = editingStudentId === student.id;
                    const isBusy = studentActionId === student.id;
                    const displayName = String(student.displayName || 'Unnamed student');
                    const dismissal = resolveStudentDismissal(student);
                    const manualDismissalType = String((student as any).dismissalProcedureType || '');
                    return (
                      <div className="squad-details-roster-row" key={student.id}>
                        <div className="squad-details-roster-avatar" aria-hidden="true">
                          {displayName.trim().slice(0, 1).toUpperCase() || '?'}
                        </div>
                        {isEditing ? (
                          <form
                            className="squad-details-roster-name-form"
                            onSubmit={(event) => { event.preventDefault(); void saveStudentName(student.id); }}
                          >
                            <input
                              autoFocus
                              value={studentNameDraft}
                              onChange={(event) => setStudentNameDraft(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === 'Escape') {
                                  setEditingStudentId(null);
                                  setStudentNameDraft('');
                                }
                              }}
                              aria-label={`Edit ${displayName}'s name`}
                              maxLength={100}
                              disabled={isBusy}
                            />
                            <button type="submit" disabled={!studentNameDraft.trim() || isBusy}>{isBusy ? 'Saving…' : 'Save'}</button>
                            <button type="button" onClick={() => { setEditingStudentId(null); setStudentNameDraft(''); }} disabled={isBusy}>Cancel</button>
                          </form>
                        ) : (
                          <button
                            type="button"
                            className="squad-details-roster-name"
                            onClick={() => { setEditingStudentId(student.id); setStudentNameDraft(displayName); setRosterError(null); }}
                            title="Click to edit student name"
                          >
                            <span>{displayName}</span>
                            <small>Click to edit name</small>
                          </button>
                        )}
                        {!isEditing && (
                          <div className="squad-details-roster-dismissal">
                            <label>
                              <span>Pickup</span>
                              <select
                                value={manualDismissalType}
                                disabled={isBusy}
                                onChange={(event) => {
                                  const value = event.target.value as DismissalProcedureType | '';
                                  void saveStudentDismissal(
                                    student.id,
                                    value || null,
                                    value === 'special' ? (student as any).dismissalProcedureNote : null,
                                  );
                                }}
                                aria-label={`${displayName} pickup information`}
                                title={dismissal.tooltip}
                              >
                                <option value="">Parent preference — {DISMISSAL_META[dismissal.type].shortLabel}</option>
                                <option value="pickup">Guardian pickup</option>
                                <option value="aftercare">Aftercare / staff</option>
                                <option value="walker">Walker / bike</option>
                                <option value="special">Special arrangement</option>
                                <option value="unassigned">Not assigned</option>
                              </select>
                            </label>
                            {manualDismissalType === 'special' && (
                              <input
                                type="text"
                                defaultValue={String((student as any).dismissalProcedureNote || '')}
                                disabled={isBusy}
                                placeholder="Special pickup instructions"
                                aria-label={`${displayName} special pickup instructions`}
                                onBlur={(event) => {
                                  if (event.target.value.trim() !== String((student as any).dismissalProcedureNote || '').trim()) {
                                    void saveStudentDismissal(student.id, 'special', event.target.value);
                                  }
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') event.currentTarget.blur();
                                }}
                              />
                            )}
                          </div>
                        )}
                        {!isEditing && (
                          <button
                            type="button"
                            className="squad-details-roster-remove"
                            onClick={() => void removeStudent(student)}
                            disabled={isBusy}
                            aria-label={`Remove ${displayName} from class`}
                          >
                            {isBusy ? 'Removing…' : 'Remove'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Location information inherited by this squad */}
          <div className="squad-details-card squad-details-location-card">
            <div className="squad-details-card-title">
              📍 {assignedLocationTitle}
              <span className="squad-details-badge teacher">Teacher editable</span>
            </div>
            <div className="squad-details-card-body">
              <div className="squad-details-form-grid">
                <div style={{ gridColumn: '1 / -1' }}>
                  <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                    This squad is assigned to {assignedLocationTitle}. These details belong to the location and are shared by every squad assigned there. Changes update the location directly.
                  </div>
                </div>
                <div className="squad-details-field">
                  <div className="squad-details-field-label">
                    WiFi username <MissingSm show={missingTeacherLogistics.has('wifiUsername')} />
                  </div>
                  <input
                    type="text"
                    value={String(draft.wifiUsername || '')}
                    onChange={e => updateDraft({ wifiUsername: e.target.value } as any, true)}
                    placeholder="WiFi username"
                  />
                </div>
                <div className="squad-details-field">
                  <div className="squad-details-field-label">
                    WiFi password <MissingSm show={missingTeacherLogistics.has('wifiPassword')} />
                  </div>
                  <input
                    type="text"
                    value={String(draft.wifiPassword || '')}
                    onChange={e => updateDraft({ wifiPassword: e.target.value } as any, true)}
                    placeholder="WiFi password"
                  />
                </div>
                <div className="squad-details-field">
                  <div className="squad-details-field-label">
                    Arrival procedure <MissingSm show={missingTeacherLogistics.has('arrivalProcedure')} />
                  </div>
                  <textarea
                    value={String((draft as any).arrivalProcedure || '')}
                    onChange={e => updateDraft({ arrivalProcedure: e.target.value } as any, true)}
                    placeholder="What should you do when you arrive?"
                  />
                </div>
                <div className="squad-details-field">
                  <div className="squad-details-field-label">
                    Dismissal procedure <MissingSm show={missingTeacherLogistics.has('dismissalProcedure')} />
                  </div>
                  <textarea
                    value={String((draft as any).dismissalProcedure || '')}
                    onChange={e => updateDraft({ dismissalProcedure: e.target.value } as any, true)}
                    placeholder="How does dismissal work?"
                  />
                </div>
                <div className="squad-details-field">
                  <div className="squad-details-field-label">
                    Late arrival rules <MissingSm show={missingTeacherLogistics.has('lateArrivalProcedure')} />
                  </div>
                  <textarea
                    value={String((draft as any).lateArrivalProcedure || '')}
                    onChange={e => updateDraft({ lateArrivalProcedure: e.target.value } as any, true)}
                    placeholder="If you arrive late, what do you do?"
                  />
                </div>
                <div className="squad-details-field">
                  <div className="squad-details-field-label">
                    Bathroom rules <MissingSm show={missingTeacherLogistics.has('bathroomRules')} />
                  </div>
                  <textarea
                    value={String(draft.bathroomRules || '')}
                    onChange={e => updateDraft({ bathroomRules: e.target.value } as any, true)}
                    placeholder="Bathroom policy/rules"
                  />
                </div>
                <div className="squad-details-field">
                  <div className="squad-details-field-label">
                    Hallway rules <MissingSm show={missingTeacherLogistics.has('hallwayRules')} />
                  </div>
                  <textarea
                    value={String((draft as any).hallwayRules || '')}
                    onChange={e => updateDraft({ hallwayRules: e.target.value } as any, true)}
                    placeholder="Hallway policy/rules"
                  />
                </div>
                <div className="squad-details-field">
                  <div className="squad-details-field-label">
                    Parking instructions <MissingSm show={missingTeacherLogistics.has('parkingInstructions')} />
                  </div>
                  <textarea
                    value={String((draft as any).parkingInstructions || '')}
                    onChange={e => updateDraft({ parkingInstructions: e.target.value } as any, true)}
                    placeholder="Where to park, where to enter, etc."
                  />
                </div>
                <div style={{ gridColumn: '1 / -1', fontWeight: 900, marginTop: 8 }}>
                  Squad-specific teaching setup
                  <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                    Applies only to this squad.
                  </div>
                </div>
                <div className="squad-details-field">
                  <div className="squad-details-field-label">
                    Projector / TV available <MissingSm show={missingTeacherLogistics.has('hasProjectorOrTv')} />
                  </div>
                  <select
                    value={String((draft as any).hasProjectorOrTv ?? '')}
                    onChange={e => {
                      const v = e.target.value;
                      updateDraft(
                        { hasProjectorOrTv: v === '' ? undefined : v === 'true' } as any,
                        true
                      );
                    }}
                  >
                    <option value="">Unknown</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
                <div className="squad-details-field" style={{ gridColumn: '1 / -1' }}>
                  <div className="squad-details-field-label">
                    How to connect to projector <MissingSm show={missingTeacherLogistics.has('projectorConnectionInstructions')} />
                  </div>
                  <textarea
                    value={String((draft as any).projectorConnectionInstructions || '')}
                    onChange={e => updateDraft({ projectorConnectionInstructions: e.target.value } as any, true)}
                    placeholder="HDMI adapter? AirPlay? Who to ask? Any gotchas?"
                  />
                </div>
                <div className="squad-details-field" style={{ gridColumn: '1 / -1' }}>
                  <div className="squad-details-field-label">Teaching notes</div>
                  <textarea
                    value={String(draft.additionalNotes || '')}
                    onChange={e => updateDraft({ additionalNotes: e.target.value } as any, true)}
                    placeholder="Anything that will help future classes go smoothly"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Program details is now folded into Class at a glance for admins */}
          {effectiveMode === 'teacher' && (
            <div className="squad-details-card">
              <div className="squad-details-card-title">
                Program details
                <span className="squad-details-badge locked">Managed by admin</span>
              </div>
              <div className="squad-details-card-body">
                {(() => {
                  const pm = String(draft.paymentMode || (squad as any)?.paymentMode || 'stripe');
                  const showFlat = pm === 'school_flat_fee';
                  const showTuition = pm === 'stripe' || pm === 'school_enrollment' || pm === 'external';
                  return (
                    <>
                      {renderReadOnlyRow(
                        'Schedule',
                        String((draft as any).scheduleKind || (squad as any)?.scheduleKind) === 'single_day'
                          ? `${formatMaybeDate((draft as any).schedule?.mainDate || (squad as any)?.schedule?.mainDate)} • ${formatTime12h(draft.schedule?.startTime || squad?.schedule?.startTime)}–${formatTime12h(draft.schedule?.endTime || squad?.schedule?.endTime)}`
                          : `${draft.schedule?.dayOfWeek || squad?.schedule?.dayOfWeek || '—'} • ${formatTime12h(draft.schedule?.startTime || squad?.schedule?.startTime)}–${formatTime12h(draft.schedule?.endTime || squad?.schedule?.endTime)}`,
                        true,
                        (String((draft as any).scheduleKind || (squad as any)?.scheduleKind) === 'single_day'
                          ? isEmptyValue((draft as any).schedule?.mainDate || (squad as any)?.schedule?.mainDate)
                          : isEmptyValue(draft.schedule?.dayOfWeek || squad?.schedule?.dayOfWeek)) || isEmptyValue(draft.schedule?.startTime || squad?.schedule?.startTime) || isEmptyValue(draft.schedule?.endTime || squad?.schedule?.endTime)
                      )}
                      {String((draft as any).scheduleKind || (squad as any)?.scheduleKind) !== 'single_day' && renderReadOnlyRow('Dates', `${formatMaybeDate(draft.schedule?.startDate || squad?.schedule?.startDate)} → ${formatMaybeDate(draft.schedule?.endDate || squad?.schedule?.endDate)}`, true, isEmptyValue(draft.schedule?.startDate || squad?.schedule?.startDate) || isEmptyValue(draft.schedule?.endDate || squad?.schedule?.endDate))}
                      {renderReadOnlyRow('Payment mode', String(draft.paymentMode || '—'), true, isEmptyValue(draft.paymentMode))}
                      {showTuition && (() => { const explicit = (draft as any).tuitionPerStudentPerClassCents ?? (squad as any)?.tuitionPerStudentPerClassCents; const effective = explicit ?? partnerDefaultProgramPriceCents; return renderReadOnlyRow('Tuition per student / hour', effective == null ? '—' : `${formatCentsUsd(effective)}${explicit == null ? ' · Partner default' : ''}`, true, effective == null); })()}
                      {showFlat && (() => { const explicit = (draft as any).flatFeePerClassCents ?? (squad as any)?.flatFeePerClassCents; const effective = explicit ?? partnerDefaultProgramPriceCents; return renderReadOnlyRow('Flat fee / class', effective == null ? '—' : `${formatCentsUsd(effective)}${explicit == null ? ' · Partner default' : ''}`, true, effective == null); })()}
                      {renderReadOnlyRow('Capacity', String(draft.capacity ?? squad?.capacity ?? 12), true, false)}
                      {(() => { const effectiveMin = draft.minEnroll ?? squad?.minEnroll ?? (partnerDefaultProgramPriceCents && partnerDefaultProgramPriceCents > 0 ? Math.floor(10000 / partnerDefaultProgramPriceCents) + 1 : null); return renderReadOnlyRow('Min enrollment', effectiveMin == null ? '—' : String(effectiveMin), true, effectiveMin == null); })()}
                      {String((draft as any).singleDayType || (squad as any)?.singleDayType) === 'demo'
                        ? renderReadOnlyRow('Demo experience', demoExperiences.find((item) => item.id === String((draft as any).demoExperienceId || (squad as any)?.demoExperienceId || ''))?.title || '—', true, isEmptyValue((draft as any).demoExperienceId || (squad as any)?.demoExperienceId))
                        : renderReadOnlyRow('Curriculum', String((draft as any).curriculumId || '—'), true, isEmptyValue((draft as any).curriculumId))}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Canonical partner contacts, with explicit read-only legacy fallback. */}
          <div className="squad-details-card">
            <div className="squad-details-card-title">
              {String((draft.partnerId ?? squad?.partnerId) || '').trim() ? 'Partner contacts' : 'Historical school contacts'}
              <span className="squad-details-badge subtle">Visible to teachers</span>
              {effectiveMode === 'admin' && partnerId && !contactFormOpen && (
                <button
                  type="button"
                  className="squad-details-contact-add"
                  onClick={() => {
                    setContactFormOpen(true);
                    setEditingContactId(null);
                    setContactDraft({ displayName: '', email: '', phone: '', role: 'primary' });
                    setContactError(null);
                  }}
                >
                  + Add contact
                </button>
              )}
            </div>
            <div className="squad-details-card-body">
              {effectiveMode === 'admin' && contactFormOpen && (
                <form className="squad-details-contact-form" onSubmit={saveContact}>
                  <div className="squad-details-contact-form-heading">{editingContactId ? 'Edit contact' : 'Add contact'}</div>
                  <input aria-label="Contact name" value={contactDraft.displayName} onChange={event => setContactDraft(current => ({ ...current, displayName: event.target.value }))} placeholder="Full name" required autoFocus />
                  <input aria-label="Contact email" type="email" value={contactDraft.email} onChange={event => setContactDraft(current => ({ ...current, email: event.target.value }))} placeholder="Email" />
                  <input aria-label="Contact phone" type="tel" value={contactDraft.phone} onChange={event => setContactDraft(current => ({ ...current, phone: event.target.value }))} placeholder="Phone" />
                  <select aria-label="Contact role" value={contactDraft.role} onChange={event => setContactDraft(current => ({ ...current, role: event.target.value as ContactRole }))}>
                    {CONTACT_ROLE_OPTIONS.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
                  </select>
                  {contactError && <div className="squad-details-contact-error" role="alert">{contactError}</div>}
                  <div className="squad-details-contact-form-actions">
                    <button type="button" onClick={resetContactEditor} disabled={contactSaving}>Cancel</button>
                    <button type="submit" className="primary" disabled={contactSaving || !contactDraft.displayName.trim()}>{contactSaving ? 'Saving…' : (editingContactId ? 'Save contact' : 'Add contact')}</button>
                  </div>
                </form>
              )}
              {contacts.length === 0 ? (
                <div className="squad-details-empty">No contacts yet.</div>
              ) : (
                <div className="squad-details-contact-list">
                  {contacts.map(c => (
                    <div key={c.id} className="squad-details-contact-row">
                      <div className="squad-details-contact-main">
                        <div className="squad-details-contact-name">{c.displayName || 'Unnamed contact'}</div>
                        <div className="squad-details-contact-meta">
                          {(c.roles || []).map(role => CONTACT_ROLE_OPTIONS.find(option => option.id === role)?.label || titleCase(String(role))).join(', ')}
                          {c.roles?.includes('other') && c.roleCustom ? ` (${c.roleCustom})` : ''}
                          {c.email ? ` • ${c.email}` : ''}
                          {c.phone ? ` • ${c.phone}` : ''}
                        </div>
                      </div>
                      {effectiveMode === 'admin' && partnerId && (
                        <button type="button" className="squad-details-contact-edit" onClick={() => beginEditContact(c)}>Edit</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Sticky save button (always available) */}
      <div className="squad-details-savebar" aria-hidden={false}>
        <div className="squad-details-savebar-inner">
          <div className="squad-details-savebar-meta">
            <div className="squad-details-savebar-title">
              {dirty ? 'Unsaved changes' : 'Up to date'}
            </div>
            <div className="squad-details-savebar-subtitle">
              {saving ? 'Saving…' : savedAt ? 'Saved just now' : 'Click to save'}
            </div>
          </div>
          <button
            className="squad-details-primary squad-details-primary-lg"
            onClick={() => void save()}
            disabled={saving || !dirty}
          >
            Save changes
          </button>
        </div>
      </div>

      {refundTarget && (
        <div className="squad-details-refund-overlay" onClick={() => { if (!refundBusy) closeRefundModal(); }}>
          <div className="squad-details-refund-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            {refundSuccess ? (
              <>
                <h3>✅ Refund issued</h3>
                <p>
                  <strong>{fmtUsdFromCents(refundSuccess.amountCents)}</strong> is on its way back to{' '}
                  <strong>{refundSuccess.email || 'the parent'}</strong>. They've been emailed a confirmation, and the
                  money usually reaches their card in 5–10 business days.
                </p>
                <div className="squad-details-refund-actions">
                  <button type="button" className="squad-details-primary" onClick={closeRefundModal}>Done</button>
                </div>
              </>
            ) : (
              <>
                <h3>Refund {paymentStudentName(refundTarget)}'s payment</h3>
                <div className="squad-details-refund-facts">
                  <div><span>Class</span><strong>{String(draft.title || squad?.title || '—')}</strong></div>
                  <div><span>Student</span><strong>{paymentStudentName(refundTarget)}</strong></div>
                  <div><span>Refund goes to</span><strong>{paymentParentLabel(refundTarget)}</strong></div>
                  <div><span>Paid</span><strong>{fmtUsdFromCents(refundTarget.amountCents)}</strong></div>
                  {Math.round(Number(refundTarget.refundedAmountCents || 0)) > 0 && (
                    <div><span>Already refunded</span><strong>{fmtUsdFromCents(Number(refundTarget.refundedAmountCents || 0))}</strong></div>
                  )}
                </div>
                <label className="squad-details-refund-amount">
                  <span>Refund amount</span>
                  <div>
                    <span aria-hidden="true">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={refundAmountInput}
                      onChange={(e) => { setRefundAmountInput(e.target.value); setRefundError(null); }}
                      disabled={refundBusy}
                      autoFocus
                    />
                    {refundAmountCentsParsed !== paymentRemainingCents(refundTarget) && (
                      <button
                        type="button"
                        className="squad-details-refund-fulllink"
                        onClick={() => setRefundAmountInput((paymentRemainingCents(refundTarget) / 100).toFixed(2))}
                        disabled={refundBusy}
                      >
                        Refund the full {fmtUsdFromCents(paymentRemainingCents(refundTarget))}
                      </button>
                    )}
                  </div>
                </label>
                <p className="squad-details-refund-warning">
                  You are sending{' '}
                  <strong>{refundAmountCentsParsed != null && refundAmountCentsParsed > 0 ? fmtUsdFromCents(refundAmountCentsParsed) : '$0.00'}</strong>{' '}
                  back to the payment method used at checkout. This cannot be undone.
                </p>
                <p className="squad-details-refund-note">
                  {paymentStudentName(refundTarget)} stays on the roster — remove them separately if they're leaving the class.
                </p>
                {refundError && <div className="squad-details-refund-error" role="alert">{refundError}</div>}
                <div className="squad-details-refund-actions">
                  <button type="button" onClick={closeRefundModal} disabled={refundBusy}>Cancel</button>
                  <button
                    type="button"
                    className="squad-details-refund-confirm"
                    onClick={() => void submitRefund()}
                    disabled={refundBusy || refundAmountCentsParsed == null || refundAmountCentsParsed <= 0 || refundAmountCentsParsed > paymentRemainingCents(refundTarget)}
                  >
                    {refundBusy
                      ? 'Refunding…'
                      : `Refund ${refundAmountCentsParsed != null && refundAmountCentsParsed > 0 ? fmtUsdFromCents(refundAmountCentsParsed) : ''}`.trim()}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {bulkRefundOpen && (
        <div className="squad-details-refund-overlay" onClick={closeBulkRefund}>
          <div className="squad-details-refund-modal squad-details-refund-modal-wide" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h3>Refund everyone in this class</h3>
            {squadIsCanceled && <p className="squad-details-refund-canceled-line">This class is canceled, so every remaining payment below will be refunded in full.</p>}
            <div className="squad-details-refund-bulk-list">
              {bulkRefundList.map((p) => {
                const progress = bulkRefundProgress[p.id];
                return (
                  <div key={p.id} className="squad-details-refund-bulk-row">
                    <div className="squad-details-payment-main">
                      <span className="squad-details-payment-student">{paymentStudentName(p)}</span>
                      <small>{paymentParentLabel(p)}</small>
                    </div>
                    <strong>{fmtUsdFromCents(paymentRemainingCents(p))}</strong>
                    <span className={`squad-details-refund-bulk-state is-${progress?.state || 'waiting'}`}>
                      {progress?.state === 'done'
                        ? '✅ Refunded'
                        : progress?.state === 'running'
                          ? 'Refunding…'
                          : progress?.state === 'error'
                            ? `⚠️ ${progress.message || 'Failed'}`
                            : bulkRefundStarted ? 'Waiting…' : ''}
                    </span>
                  </div>
                );
              })}
            </div>
            {!bulkRefundStarted ? (
              <>
                <p className="squad-details-refund-warning">
                  You are sending{' '}
                  <strong>{fmtUsdFromCents(bulkRefundList.reduce((sum, p) => sum + paymentRemainingCents(p), 0))}</strong>{' '}
                  back to <strong>{bulkRefundList.length} {bulkRefundList.length === 1 ? 'family' : 'families'}</strong> — each refund
                  goes to that parent's original payment method, and every parent gets a confirmation email. This cannot be undone.
                </p>
                <p className="squad-details-refund-note">Students stay on the roster.</p>
                <div className="squad-details-refund-actions">
                  <button type="button" onClick={closeBulkRefund}>Cancel</button>
                  <button type="button" className="squad-details-refund-confirm" onClick={() => void runBulkRefund()}>
                    Refund {bulkRefundList.length} {bulkRefundList.length === 1 ? 'payment' : 'payments'} ({fmtUsdFromCents(bulkRefundList.reduce((sum, p) => sum + paymentRemainingCents(p), 0))})
                  </button>
                </div>
              </>
            ) : (
              <div className="squad-details-refund-actions">
                <button
                  type="button"
                  className="squad-details-primary"
                  onClick={closeBulkRefund}
                  disabled={bulkRefundRunning}
                >
                  {bulkRefundRunning ? 'Refunding — keep this open…' : 'Done'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmState.open}
        onClose={() => {
          setConfirmState(s => ({ ...s, open: false }));
          const r = confirmResolverRef.current;
          confirmResolverRef.current = null;
          r?.(false);
        }}
        onConfirm={() => {
          setConfirmState(s => ({ ...s, open: false }));
          const r = confirmResolverRef.current;
          confirmResolverRef.current = null;
          r?.(true);
        }}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText || 'Cancel'}
      />
    </div>
  );
};

export default SquadDetailsPage;
